/**
 * Scheduler Admin Dashboard
 * Full schedule view, run-scheduler button, and results table.
 * Accessible to: Scheduler.Admin, Survey.Admin
 */

import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  type DragEndEvent,
} from '@dnd-kit/core';
import { schedulerApi, SchedulerRun, SchedulerSection, getSlotLabel, InstructorLookup, SmsSyncStatus, InstructorSyncStatus, InstructorSyncResult, PresenceUser, PresenceResponse } from '../../services/scheduler-api';
import { getCurrentUser } from '../../auth/LoginButton';
import { ConstraintsEditor } from './ConstraintsEditor';
import { DataManagementPanel } from './DataManagementPanel';
import { ImpersonationAuditLog } from './ImpersonationAuditLog';
import { ChangeAuditLog } from './ChangeAuditLog';
import { InstructorScheduleView } from './InstructorScheduleView';
import { InstructorProfileView } from './InstructorProfileView';

const CARD = {
  background: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
  borderRadius: '12px',
  padding: '1.5rem',
} as const;

const BTN_PRIMARY = {
  padding: '10px 24px',
  background: 'var(--accent)',
  color: 'var(--button-text)',
  border: 'none',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'opacity 0.2s',
} as const;

const BTN_SECONDARY = {
  padding: '10px 20px',
  background: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  border: '1px solid var(--card-border)',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
} as const;

// ---------------------------------------------------------------------------
// Active Users Panel (super admin only)
// ---------------------------------------------------------------------------
function ActiveUsersPanel() {
  const [data, setData] = useState<PresenceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    schedulerApi.getActiveUsers()
      .then(d => { setData(d); setError(null); })
      .catch(e => setError(e?.response?.data?.error ?? e.message ?? 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 30_000);
    return () => clearInterval(interval);
  }, []);

  const formatRelative = (iso: string) => {
    const diffMs = Date.now() - new Date(iso).getTime();
    const diffMin = Math.floor(diffMs / 60_000);
    if (diffMin < 1) return 'just now';
    if (diffMin === 1) return '1 min ago';
    return `${diffMin} min ago`;
  };

  return (
    <div style={{ ...CARD }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <span style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>
          👥 Active Users <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '400' }}>(active within 5 min)</span>
        </span>
        <button onClick={load} style={{ fontSize: '12px', padding: '4px 12px', background: 'var(--bg-secondary)', border: '1px solid var(--card-border)', borderRadius: '6px', cursor: 'pointer', color: 'var(--text-primary)' }}>
          ↻ Refresh
        </button>
      </div>

      {loading && !data && <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</p>}
      {error && <p style={{ color: '#f87171', fontSize: '13px' }}>{error}</p>}

      {data && (
        <>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 0.75rem 0' }}>
            {data.users.length === 0 ? 'No active users right now.' : `${data.users.length} user${data.users.length === 1 ? '' : 's'} currently active`}
            {' · '}<span>fetched {formatRelative(data.fetched_at)}</span>
          </p>
          {data.users.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ color: 'var(--text-secondary)', borderBottom: '1px solid var(--card-border)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Email</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Current Page</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map(u => (
                  <tr key={u.email} style={{ borderBottom: '1px solid var(--card-border)' }}>
                    <td style={{ padding: '7px 8px', color: 'var(--text-primary)' }}>{u.name || '—'}</td>
                    <td style={{ padding: '7px 8px', color: 'var(--text-secondary)' }}>{u.email}</td>
                    <td style={{ padding: '7px 8px', color: 'var(--accent)' }}>
                      <code style={{ fontSize: '12px' }}>{u.current_page}</code>
                    </td>
                    <td style={{ padding: '7px 8px', color: 'var(--text-secondary)' }}>{formatRelative(u.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drag-and-drop helper components for the Instructor Matrix
// ---------------------------------------------------------------------------

function DraggableSectionChip({ section, children }: { section: SchedulerSection; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: section.section_id });
  return (
    <span
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 7px',
        borderRadius: '4px',
        fontWeight: '600',
        whiteSpace: 'nowrap',
        cursor: 'grab',
        opacity: isDragging ? 0.3 : 1,
        background: 'rgba(var(--accent-rgb,255,215,0),0.1)',
        border: '1px solid rgba(var(--accent-rgb,255,215,0),0.25)',
        color: 'var(--accent)',
        userSelect: 'none',
      }}
    >
      {children}
    </span>
  );
}

function DroppableCell({
  id,
  children,
  style,
}: {
  id: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <td
      ref={setNodeRef}
      style={{
        padding: '8px 12px',
        verticalAlign: 'top',
        outline: isOver ? '2px solid var(--accent)' : undefined,
        background: isOver ? 'rgba(var(--accent-rgb,255,215,0),0.08)' : undefined,
        transition: 'background 0.1s, outline 0.1s',
        ...style,
      }}
    >
      {children}
    </td>
  );
}

export function SchedulerAdminDashboard() {
  // Active tab
  const currentUser = getCurrentUser();
  const isSuperAdmin = currentUser?.email?.toLowerCase() === 'jadell.jack@lauruscollege.edu';
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = location.pathname.startsWith('/ECM') ? '/ECM/Dashboard/scheduler' : '/Scheduler';
  const activeTab = ((): 'scheduler' | 'data' | 'audit' | 'activity' => {
    const suffix = location.pathname.slice(basePath.length);
    if (suffix === '/data' || suffix.startsWith('/data/')) return 'data';
    if (suffix === '/audit' || suffix.startsWith('/audit/')) return 'audit';
    if (suffix === '/activity') return 'activity';
    return 'scheduler';
  })();

  // Run form state
  const [showConstraints, setShowConstraints] = useState(false);
  const [termStart, setTermStart] = useState(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}-01`;
  });
  const [termEnd, setTermEnd] = useState(() => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}-${String(lastDay).padStart(2, '0')}`;
  });
  const [termName, setTermName] = useState('');

  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);

  // Results state
  const [runs, setRuns] = useState<SchedulerRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<SchedulerRun | null>(null);
  const [loadingRuns, setLoadingRuns] = useState(true);

  // Section filter
  const [filterCourse, setFilterCourse] = useState('');
  const [filterInstructor, setFilterInstructor] = useState('');
  const [loadingSelected, setLoadingSelected] = useState(false);
  const [expandedSectionIds, setExpandedSectionIds] = useState<Set<string>>(new Set());
  const [showSections, setShowSections] = useState(false);
  const [showInstructorMatrix, setShowInstructorMatrix] = useState(false);
  const [showStudentMatrix, setShowStudentMatrix] = useState(false);
  const [showAllRuns, setShowAllRuns] = useState(false);
  const [filterStudent, setFilterStudent] = useState('');
  const [matrixSort, setMatrixSort] = useState<{ col: 'student' | 'name' | 'db'; dir: 'asc' | 'desc' }>({ col: 'student', dir: 'asc' });
  const [hideEmptyInstructorRows, setHideEmptyInstructorRows] = useState(false);
  const [hideNoStudentInstructors, setHideNoStudentInstructors] = useState(false);
  const [instructorLookup, setInstructorLookup] = useState<Record<string, { name: string; primary_area: string | null; faculty_type?: 'FT' | 'Adjunct' }>>({});
  // Credential lookup: instructorEmail (lowercase) → Set of credentialed course codes (lowercase)
  const [credentialLookup, setCredentialLookup] = useState<Record<string, Set<string>>>({});

  // Matrix editing state
  const [activeDragSection, setActiveDragSection] = useState<SchedulerSection | null>(null);
  const [movingSectionPending, setMovingSectionPending] = useState(false);
  const [matrixEditError, setMatrixEditError] = useState<string | null>(null);
  const [excelUrlsByRun, setExcelUrlsByRun] = useState<Record<string, string>>({});
  // Student move modal
  type StudentMoveTarget = { StudentNo: string; DB: string; from_section_id: string; course: string; first_name?: string; last_name?: string } | null;
  const [movingStudent, setMovingStudent] = useState<StudentMoveTarget>(null);
  const [studentMoveTarget, setStudentMoveTarget] = useState<string>('');
  const [studentMovePending, setStudentMovePending] = useState(false);
  const [moveCrossCourse, setMoveCrossCourse] = useState(false);
  const [forceOverCapacity, setForceOverCapacity] = useState(false);
  // Delete section modal
  const [deletingSectionTarget, setDeletingSectionTarget] = useState<SchedulerSection | null>(null);
  const [deleteSectionPending, setDeleteSectionPending] = useState(false);
  const [deleteSectionError, setDeleteSectionError] = useState<string | null>(null);
  const [deleteSectionForce, setDeleteSectionForce] = useState(false);
  // Create section modal
  const [showCreateSection, setShowCreateSection] = useState(false);
  const [createSectionPending, setCreateSectionPending] = useState(false);
  const [createSectionError, setCreateSectionError] = useState<string | null>(null);
  const [createSectionCourse, setCreateSectionCourse] = useState('');
  const [createSectionInstructor, setCreateSectionInstructor] = useState('');
  const [createSectionSlot, setCreateSectionSlot] = useState('');
  const [createSectionCapacity, setCreateSectionCapacity] = useState('30');
  // Unmatched student manual assignment modal
  type AssignTarget = { StudentNo: string; DB: string; course: string; first_name?: string; last_name?: string } | null;
  const [assigningStudent, setAssigningStudent] = useState<AssignTarget>(null);
  // Assign instructor to unstaffed section
  const [assigningInstructorToSection, setAssigningInstructorToSection] = useState<string | null>(null);
  const [studentAssignTarget, setStudentAssignTarget] = useState<string>('');
  const [studentAssignPending, setStudentAssignPending] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  const toggleSection = (id: string) => {
    setExpandedSectionIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Handle section drag-drop: reassign section to a new instructor/slot cell
  const handleSectionDragEnd = async (event: DragEndEvent) => {
    setActiveDragSection(null);
    const { active, over } = event;
    if (!over || !selectedRun) return;

    const sectionId = active.id as string;
    // Drop target id is encoded as "instructor||slot"
    const [newInstructor, newSlot] = (over.id as string).split('||');
    if (!newInstructor || !newSlot) return;

    const section = selectedRun.sections.find(s => s.section_id === sectionId);
    if (!section) return;
    if (section.instructor === newInstructor && section.slot === newSlot) return; // no change

    setMovingSectionPending(true);
    setMatrixEditError(null);
    try {
      const result = await schedulerApi.moveSection(selectedRun.run_id, { section_id: sectionId, new_instructor: newInstructor, new_slot: newSlot });
      if (result.excel_url) setExcelUrlsByRun(prev => ({ ...prev, [selectedRun.run_id]: result.excel_url! }));
      // Refresh run data
      const updated = await schedulerApi.getResult(selectedRun.run_id);
      setSelectedRun(updated);
      setRuns(prev => prev.map(r => r.run_id === updated.run_id ? updated : r));
    } catch (e: any) {
      setMatrixEditError(e?.response?.data?.error ?? e.message ?? 'Failed to move section');
    } finally {
      setMovingSectionPending(false);
    }
  };

  // Assign an instructor to an unstaffed section
  const handleAssignInstructorToSection = async (section: SchedulerSection, instructorEmail: string) => {
    if (!selectedRun || !section.slot) return;
    setAssigningInstructorToSection(null);
    setMovingSectionPending(true);
    setMatrixEditError(null);
    try {
      const result = await schedulerApi.moveSection(selectedRun.run_id, {
        section_id: section.section_id,
        new_instructor: instructorEmail,
        new_slot: section.slot,
      });
      if (result.excel_url) setExcelUrlsByRun(prev => ({ ...prev, [selectedRun.run_id]: result.excel_url! }));
      const updated = await schedulerApi.getResult(selectedRun.run_id);
      setSelectedRun(updated);
      setRuns(prev => prev.map(r => r.run_id === updated.run_id ? updated : r));
    } catch (e: any) {
      setMatrixEditError(e?.response?.data?.error ?? e.message ?? 'Failed to assign instructor');
    } finally {
      setMovingSectionPending(false);
    }
  };

  // Handle student move confirmation (click-based)
  const handleStudentMove = async () => {
    if (!movingStudent || !studentMoveTarget || !selectedRun) return;
    setStudentMovePending(true);
    setMatrixEditError(null);
    try {
      const result = await schedulerApi.moveStudent(selectedRun.run_id, {
        student_no: movingStudent.StudentNo,
        db: movingStudent.DB,
        from_section_id: movingStudent.from_section_id,
        to_section_id: studentMoveTarget,
      });
      if (result.excel_url) setExcelUrlsByRun(prev => ({ ...prev, [selectedRun.run_id]: result.excel_url! }));
      const updated = await schedulerApi.getResult(selectedRun.run_id);
      setSelectedRun(updated);
      setRuns(prev => prev.map(r => r.run_id === updated.run_id ? updated : r));
      setMovingStudent(null);
      setStudentMoveTarget('');
      setMoveCrossCourse(false);
      setForceOverCapacity(false);
    } catch (e: any) {
      setMatrixEditError(e?.response?.data?.error ?? e.message ?? 'Failed to move student');
    } finally {
      setStudentMovePending(false);
    }
  };

  // Handle delete section
  const handleDeleteSection = async (force: boolean) => {
    if (!deletingSectionTarget || !selectedRun) return;
    setDeleteSectionPending(true);
    setDeleteSectionError(null);
    try {
      const result = await schedulerApi.deleteSection(selectedRun.run_id, deletingSectionTarget.section_id, force);
      if (result.excel_url) setExcelUrlsByRun(prev => ({ ...prev, [selectedRun.run_id]: result.excel_url! }));
      const updated = await schedulerApi.getResult(selectedRun.run_id);
      setSelectedRun(updated);
      setRuns(prev => prev.map(r => r.run_id === updated.run_id ? updated : r));
      setDeletingSectionTarget(null);
      setDeleteSectionForce(false);
    } catch (e: any) {
      const msg = e?.response?.data?.error ?? e.message ?? 'Failed to delete section';
      setDeleteSectionError(msg);
      // If the error indicates students are enrolled and force wasn't set, prompt for force
      if (e?.response?.status === 409) {
        setDeleteSectionForce(true);
      }
    } finally {
      setDeleteSectionPending(false);
    }
  };

  // Handle create section
  const handleCreateSection = async () => {
    if (!selectedRun || !createSectionCourse.trim()) return;
    setCreateSectionPending(true);
    setCreateSectionError(null);
    try {
      const result = await schedulerApi.createSection(selectedRun.run_id, {
        course: createSectionCourse.trim(),
        instructor: createSectionInstructor || null,
        slot: createSectionSlot || null,
        capacity: parseInt(createSectionCapacity) || 30,
      });
      if (result.excel_url) setExcelUrlsByRun(prev => ({ ...prev, [selectedRun.run_id]: result.excel_url! }));
      const updated = await schedulerApi.getResult(selectedRun.run_id);
      setSelectedRun(updated);
      setRuns(prev => prev.map(r => r.run_id === updated.run_id ? updated : r));
      setShowCreateSection(false);
      setCreateSectionCourse('');
      setCreateSectionInstructor('');
      setCreateSectionSlot('');
      setCreateSectionCapacity('30');
    } catch (e: any) {
      setCreateSectionError(e?.response?.data?.error ?? e.message ?? 'Failed to create section');
    } finally {
      setCreateSectionPending(false);
    }
  };

  // Handle manual assignment of unmatched student to a section
  const handleStudentAssign = async () => {
    if (!assigningStudent || !studentAssignTarget || !selectedRun) return;
    setStudentAssignPending(true);
    setAssignError(null);
    try {
      const result = await schedulerApi.assignStudent(selectedRun.run_id, {
        student_no: assigningStudent.StudentNo,
        db: assigningStudent.DB,
        course: assigningStudent.course,
        to_section_id: studentAssignTarget,
      });
      if (result.excel_url) setExcelUrlsByRun(prev => ({ ...prev, [selectedRun.run_id]: result.excel_url! }));
      const updated = await schedulerApi.getResult(selectedRun.run_id);
      setSelectedRun(updated);
      setRuns(prev => prev.map(r => r.run_id === updated.run_id ? updated : r));
      setAssigningStudent(null);
      setStudentAssignTarget('');
    } catch (e: any) {
      setAssignError(e?.response?.data?.error ?? e.message ?? 'Failed to assign student');
    } finally {
      setStudentAssignPending(false);
    }
  };

  // Data refresh
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null);
  const [dataRefreshKey, setDataRefreshKey] = useState(0);

  // SMS sync state
  const [smsStatus, setSmsStatus] = useState<SmsSyncStatus | null>(null);
  const [smsSyncing, setSmsSyncing] = useState(false);
  const [smsSyncMsg, setSmsSyncMsg] = useState<string | null>(null);
  const [smsSyncError, setSmsSyncError] = useState<string | null>(null);
  const [smsSyncDuration, setSmsSyncDuration] = useState<string | null>(null);
  const [smsElapsed, setSmsElapsed] = useState(0);
  const smsStartRef = useRef<number | null>(null);
  const smsTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Instructor sync state
  const [instrSyncStatus, setInstrSyncStatus] = useState<InstructorSyncStatus | null>(null);
  const [instrSyncing, setInstrSyncing] = useState(false);
  const [instrSyncMsg, setInstrSyncMsg] = useState<string | null>(null);
  const [instrSyncError, setInstrSyncError] = useState<string | null>(null);
  const [instrElapsed, setInstrElapsed] = useState(0);
  const instrStartRef = useRef<number | null>(null);
  const instrTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Polling for async run completion
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [settingLive, setSettingLive] = useState(false);
  const [deletingRunId, setDeletingRunId] = useState<string | null>(null);

  // Elapsed timer
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const runStartRef = useRef<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Impersonation
  const [instructorList, setInstructorList] = useState<InstructorLookup[]>([]);
  const [impersonatedInstructor, setImpersonatedInstructor] = useState<InstructorLookup | null>(null);
  const [impersonationTab, setImpersonationTab] = useState<'schedule' | 'profile'>('schedule');

  // Start/stop the elapsed timer whenever `running` changes
  useEffect(() => {
    if (running) {
      runStartRef.current = Date.now();
      setElapsedSeconds(0);
      timerRef.current = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - (runStartRef.current ?? Date.now())) / 1000));
      }, 1000);
    } else {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }
    return () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  }, [running]);

  // Force-clear running state if the selected run reaches a terminal status
  // (handles cases where cancel was clicked on an already-completed run)
  useEffect(() => {
    if (!selectedRun) return;
    const terminal = selectedRun.status === 'complete' || selectedRun.status === 'failed' || selectedRun.status === 'cancelled';
    if (terminal && running) {
      setRunning(false);
      setCancelling(false);
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      if (selectedRun.status === 'failed') setRunError(selectedRun.error || 'Run failed');
    }
  }, [selectedRun?.status]);

  useEffect(() => {
    loadRuns();
    loadInstructorList();
    // Load last SMS sync status
    schedulerApi.getSmsSyncStatus().then(s => setSmsStatus(s)).catch(() => {});
    // Load last instructor sync status
    schedulerApi.getInstructorSyncStatus().then(s => setInstrSyncStatus(s)).catch(() => {});
    // Clear polling on unmount
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const loadInstructorList = async () => {
    try {
      const data = await schedulerApi.getInstructors();
      setInstructorList(
        data.instructors
          .filter(i => !i.do_not_schedule)
          .map(i => ({ row_key: i.row_key, name: i.name, email: i.email }))
      );
      // Build email-keyed lookup for matrix use (name + primary_area + faculty_type)
      const lookup: Record<string, { name: string; primary_area: string | null; faculty_type?: 'FT' | 'Adjunct' }> = {};
      for (const i of data.instructors) {
        if (i.email) lookup[i.email.toLowerCase()] = { name: i.name, primary_area: i.primary_area, faculty_type: i.faculty_type };
      }
      setInstructorLookup(lookup);
    } catch {
      // Non-critical — dropdown will be empty
    }
    // Load credentials for unauthorized-course detection
    try {
      const credData = await schedulerApi.getCredentials();
      const cl: Record<string, Set<string>> = {};
      for (const cred of credData.credentials) {
        const em = (cred.instructor_email ?? '').toLowerCase();
        if (!em) continue;
        if (!cl[em]) cl[em] = new Set();
        cl[em].add(cred.course_code.toLowerCase());
      }
      setCredentialLookup(cl);
    } catch {
      // Non-critical
    }
  };

  const startImpersonation = async (instr: InstructorLookup) => {
    setImpersonatedInstructor(instr);
    setImpersonationTab('schedule');
    try {
      await schedulerApi.logImpersonation({
        instructor_email: instr.email,
        instructor_name: instr.name,
        action: 'start',
      });
    } catch {
      // Non-blocking
    }
  };

  const exitImpersonation = async () => {
    if (impersonatedInstructor) {
      try {
        await schedulerApi.logImpersonation({
          instructor_email: impersonatedInstructor.email,
          instructor_name: impersonatedInstructor.name,
          action: 'end',
        });
      } catch {}
    }
    setImpersonatedInstructor(null);
  };

  const loadRuns = async () => {
    setLoadingRuns(true);
    try {
      const data = await schedulerApi.getResults();
      // Deduplicate by run_id (guards against frontend-state drift)
      const seen = new Set<string>();
      const unique = (data.runs || []).filter(r => { if (seen.has(r.run_id)) return false; seen.add(r.run_id); return true; });
      setRuns(unique);
      if (data.runs?.length) {
        const first = data.runs[0];
        // Auto-select the most recent run with full details
        await selectRun(first);

        // If the most recent run is still in progress, resume polling automatically
        if ((first.status === 'pending' || first.status === 'running') && !pollingRef.current) {
          setRunning(true);
          pollingRef.current = setInterval(async () => {
            try {
              const result = await schedulerApi.getResult(first.run_id);
              setRuns(prev => prev.map(r => r.run_id === result.run_id ? result : r));
              setSelectedRun(result);
              if (result.status === 'complete' || result.status === 'failed' || result.status === 'cancelled') {
                clearInterval(pollingRef.current!);
                pollingRef.current = null;
                setRunning(false);
                setCancelling(false);
                if (result.status === 'failed') setRunError(result.error || 'Run failed');
              }
            } catch {
              // keep polling on transient errors
            }
          }, 2000);
        }
      }
    } catch {
      // No runs yet is fine
    } finally {
      setLoadingRuns(false);
    }
  };

  const selectRun = async (run: SchedulerRun) => {
    // If sections already loaded (e.g. freshly run), use as-is
    if (run.sections?.length) {
      setSelectedRun(run);
      return;
    }
    // Otherwise fetch full details from the API
    setLoadingSelected(true);
    try {
      const full = await schedulerApi.getResult(run.run_id);
      setSelectedRun(full);
      // Update the summary in the runs list so re-clicks are instant
      setRuns(prev => prev.map(r => r.run_id === full.run_id ? full : r));
    } catch {
      setSelectedRun(run); // fall back to summary
    } finally {
      setLoadingSelected(false);
    }
  };

  const handleRunScheduler = async () => {
    if (!termStart || !termEnd) return;
    setRunning(true);
    setRunError(null);
    try {
      const pending = await schedulerApi.runScheduler({
        term_start: termStart,
        term_end: termEnd,
        term_name: termName || undefined,
      });
      setRuns(prev => [pending, ...prev.filter(r => r.run_id !== pending.run_id)]);
      setSelectedRun(pending);

      // Poll every 3 seconds until the run completes or fails.
      // Guard against duplicate intervals (e.g. resumed from loadRuns).
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      pollingRef.current = setInterval(async () => {
        try {
          const result = await schedulerApi.getResult(pending.run_id);
          setRuns(prev => prev.map(r => r.run_id === result.run_id ? result : r));
          setSelectedRun(result);
          if (result.status === 'complete' || result.status === 'failed' || result.status === 'cancelled') {
            clearInterval(pollingRef.current!);
            pollingRef.current = null;
            setRunning(false);
            setCancelling(false);
            if (result.status === 'failed') {
              setRunError(result.error || 'Run failed');
            }
          }
        } catch {
          // keep polling on transient errors
        }
      }, 2000);
    } catch (e: any) {
      setRunError(e?.response?.data?.message || e?.message || 'Run failed');
      setRunning(false);
    }
  };

  const handleCancelRun = async () => {
    if (!selectedRun) return;
    setCancelling(true);
    try {
      const res = await schedulerApi.cancelRun(selectedRun.run_id);
      // If the backend reports the run is already in a terminal state, clear immediately
      if ((res as any).status && ['complete', 'failed', 'cancelled'].includes((res as any).status)) {
        setCancelling(false);
        setRunning(false);
        if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
        schedulerApi.getResult(selectedRun.run_id).then(result => {
          setSelectedRun(result);
          setRuns(prev => prev.map(r => r.run_id === result.run_id ? result : r));
        }).catch(() => {});
        return;
      }
    } catch {
      // Cancellation request best-effort — polling will still detect the status change
    }
    // Force-clear the UI after 15 seconds regardless of backend response.
    // Handles cases where the run already completed and the thread never checks the cancel flag.
    setTimeout(() => {
      setCancelling(false);
      setRunning(false);
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      if (selectedRun) {
        schedulerApi.getResult(selectedRun.run_id).then(result => {
          setSelectedRun(result);
          setRuns(prev => prev.map(r => r.run_id === result.run_id ? result : r));
        }).catch(() => {});
      }
    }, 15000);
  };

  const handleSetLive = async () => {
    if (!selectedRun) return;
    setSettingLive(true);
    try {
      const res = await schedulerApi.setLiveRun(selectedRun.run_id);
      if (res.ok) {
        setRuns(prev => prev.map(r => ({ ...r, is_live: r.run_id === selectedRun.run_id })));
        setSelectedRun(prev => prev ? { ...prev, is_live: true } : prev);
      }
    } catch {
      // Non-blocking
    } finally {
      setSettingLive(false);
    }
  };

  const handleUnsetLive = async () => {
    if (!selectedRun) return;
    setSettingLive(true);
    try {
      const res = await schedulerApi.unsetLiveRun(selectedRun.run_id);
      if (res.ok) {
        setRuns(prev => prev.map(r => r.run_id === selectedRun.run_id ? { ...r, is_live: false } : r));
        setSelectedRun(prev => prev ? { ...prev, is_live: false } : prev);
      }
    } catch {
      // Non-blocking
    } finally {
      setSettingLive(false);
    }
  };

  const handleDeleteRun = async (runId: string) => {
    setDeletingRunId(runId);
    try {
      await schedulerApi.deleteRun(runId);
      setRuns(prev => prev.filter(r => r.run_id !== runId));
      if (selectedRun?.run_id === runId) setSelectedRun(null);
    } catch {
      // Non-blocking — if it fails the run stays in the list
    } finally {
      setDeletingRunId(null);
    }
  };

  const handleDataRefresh = async () => {    setRefreshing(true);
    setRefreshMsg(null);
    try {
      const r = await schedulerApi.refreshData();
      setRefreshMsg(`Tables refreshed at ${new Date(r.refreshed_at).toLocaleTimeString()}`);
      setDataRefreshKey(prev => prev + 1);
    } catch (e: any) {
      setRefreshMsg('Refresh failed: ' + (e?.response?.data?.message || e?.message));
    } finally {
      setRefreshing(false);
    }
  };

  const handleSmsSync = async () => {
    setSmsSyncing(true);
    setSmsSyncMsg(null);
    setSmsSyncError(null);
    setSmsSyncDuration(null);
    setSmsElapsed(0);
    smsStartRef.current = Date.now();
    smsTimerRef.current = setInterval(() => {
      setSmsElapsed(Math.floor((Date.now() - (smsStartRef.current ?? Date.now())) / 1000));
    }, 1000);
    try {
      const r = await schedulerApi.triggerSmsSync();
      if (r.already_current) {
        const wallSec = ((Date.now() - (smsStartRef.current ?? Date.now())) / 1000).toFixed(1);
        setSmsSyncDuration(wallSec + 's');
        setSmsSyncMsg(`Information already current or up to date. (${wallSec}s)`);
        return;
      }
      // Poll for completion
      const poll = async () => {
        let done = false;
        while (!done) {
          await new Promise(res => setTimeout(res, 3000));
          try {
            const progress = await schedulerApi.getSmsSyncProgress();
            if (progress.status === 'done' || progress.status === 'error') {
              done = true;
              const wallMs = Date.now() - (smsStartRef.current ?? Date.now());
              const wallSec = (wallMs / 1000).toFixed(1);
              setSmsSyncDuration(wallSec + 's');
              if (progress.status === 'error') {
                const errMsg = (progress as any).error || 'Sync failed';
                setSmsSyncError('❌ ' + errMsg);
              } else {
                const dbs = Object.entries(progress.rows_written || {})
                  .map(([db, cnt]) => `${db}: ${(cnt as number).toLocaleString()} rows`)
                  .join(', ');
                setSmsSyncMsg(`✅ Sync complete — ${dbs} (${wallSec}s)`);
                setSmsStatus(prev => prev
                  ? { ...prev, sms_synced_at: progress.sms_synced_at,
                      sync_duration_secs: parseFloat(wallSec),
                      bachelor_rows: progress.rows_written?.['Bachelor'] ?? prev.bachelor_rows,
                      nevada_rows: progress.rows_written?.['Nevada'] ?? prev.nevada_rows,
                    }
                  : { sms_synced_at: progress.sms_synced_at, sims_edited_at: null, bachelor_configured: true, nevada_configured: true,
                      sync_duration_secs: parseFloat(wallSec),
                      bachelor_rows: progress.rows_written?.['Bachelor'],
                      nevada_rows: progress.rows_written?.['Nevada'],
                    }
                );
                if (progress.errors?.length) {
                  setSmsSyncError('⚠️ Partial errors: ' + progress.errors.join('; '));
                }
              }
            }
          } catch { /* keep polling */ }
        }
      };
      await poll();
    } catch (e: any) {
      const wallMs = Date.now() - (smsStartRef.current ?? Date.now());
      setSmsSyncDuration((wallMs / 1000).toFixed(1) + 's');
      const msg = e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Sync failed';
      setSmsSyncError('❌ ' + msg);
    } finally {
      if (smsTimerRef.current) clearInterval(smsTimerRef.current);
      smsTimerRef.current = null;
      smsStartRef.current = null;
      setSmsSyncing(false);
    }
  };

  const handleInstructorSync = async () => {
    setInstrSyncing(true);
    setInstrSyncMsg(null);
    setInstrSyncError(null);
    setInstrElapsed(0);
    instrStartRef.current = Date.now();
    instrTimerRef.current = setInterval(() => {
      setInstrElapsed(Math.floor((Date.now() - (instrStartRef.current ?? Date.now())) / 1000));
    }, 1000);
    try {
      const r = await schedulerApi.triggerInstructorSync();
      if (r.status === 'running') {
        // Poll for completion
        let done = false;
        while (!done) {
          await new Promise(res => setTimeout(res, 3000));
          try {
            const progress = await schedulerApi.getInstructorSyncProgress();
            if (progress.status === 'done' || progress.status === 'error') {
              done = true;
              const wallSec = ((Date.now() - (instrStartRef.current ?? Date.now())) / 1000).toFixed(1);
              if (progress.status === 'error') {
                setInstrSyncError('❌ ' + (progress.message || 'Sync failed'));
              } else {
                setInstrSyncMsg(`✅ ${progress.message || `${progress.rows_written ?? 0} instructors ready (${wallSec}s)`}`);
                setInstrSyncStatus(prev => ({
                  ...(prev ?? { bachelor_configured: true, nevada_configured: true }),
                  instructor_synced_at: progress.completed_at ?? new Date().toISOString(),
                  sync_duration_secs: parseFloat(wallSec),
                  rows_written: progress.rows_written ?? 0,
                  source: progress.source ?? null,
                }));
                if (progress.errors?.length) {
                  setInstrSyncError('⚠️ Partial errors: ' + progress.errors.join('; '));
                }
              }
            }
          } catch { /* keep polling */ }
        }
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Sync failed';
      setInstrSyncError('❌ ' + msg);
    } finally {
      if (instrTimerRef.current) clearInterval(instrTimerRef.current);
      instrTimerRef.current = null;
      instrStartRef.current = null;
      setInstrSyncing(false);
    }
  };

  const exportCsv = (sections: SchedulerSection[]) => {
    const rows = [
      ['Course', 'Instructor', 'Time Slot', 'Start', 'End', 'Enrolled', 'Capacity'],
      ...(sections || []).map(s => [
        s.course,
        s.instructor ?? '(Unstaffed)',
        getSlotLabel(s.slot),
        s.start,
        s.end,
        s.enrolled,
        s.capacity,
      ]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `schedule-${selectedRun?.term_start ?? 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Unique students placed (normalized, cross-DB-deduped)
  const uniqueStudentCount = new Set(
    (selectedRun?.sections ?? []).flatMap(s =>
      (s.students ?? []).map(st => {
        const n = parseFloat(st.StudentNo);
        return isNaN(n) ? st.StudentNo : String(Math.round(n));
      })
    )
  ).size;

  // Filtered sections from the selected run
  const filteredSections: SchedulerSection[] = (selectedRun?.sections ?? []).filter(s => {
    const matchCourse = !filterCourse || s.course.toLowerCase().includes(filterCourse.toLowerCase());
    const matchInstructor = !filterInstructor || (s.instructor ?? '').toLowerCase().includes(filterInstructor.toLowerCase());
    return matchCourse && matchInstructor;
  });

  // Effective unstaffed count: only sections with enrolled students (ignore 0-student unstaffed)
  const effectiveUnstaffedCount = (selectedRun?.sections ?? []).filter(s => !s.instructor && (s.enrolled ?? 0) > 0).length;
  // Students enrolled in unstaffed sections (unique)
  const studentsInUnstaffed = new Set(
    (selectedRun?.sections ?? [])
      .filter(s => !s.instructor && (s.enrolled ?? 0) > 0)
      .flatMap(s => (s.students ?? []).map(st => `${st.StudentNo}||${st.DB}`))
  ).size;

  const topRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={topRef}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--accent)', fontSize: '28px' }}>🗓️ Course Scheduler</h2>
          <p style={{ margin: '0.4rem 0 0 0', color: 'var(--text-secondary)' }}>
            Generate the section schedule for a term
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {/* Impersonation dropdown */}
          <div>
            <label style={{ display: 'block', fontSize: '10px', color: 'var(--text-secondary)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              🕵️ Impersonate
            </label>
            <select
              value={impersonatedInstructor?.row_key ?? ''}
              onChange={e => {
                const rk = e.target.value;
                if (!rk) {
                  if (impersonatedInstructor) exitImpersonation();
                } else {
                  const instr = instructorList.find(i => i.row_key === rk);
                  if (instr) startImpersonation(instr);
                }
              }}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: impersonatedInstructor ? '2px solid #facc15' : '1px solid var(--card-border)',
                background: impersonatedInstructor ? 'rgba(250,204,21,0.08)' : 'var(--bg-secondary)',
                color: impersonatedInstructor ? '#facc15' : 'var(--text-primary)',
                fontSize: '13px',
                minWidth: '200px',
                cursor: 'pointer',
              }}
            >
              <option value="">— Select instructor —</option>
              {instructorList.map(i => (
                <option key={i.row_key} value={i.row_key}>{i.name}</option>
              ))}
            </select>
          </div>
          {/* Constraints button — only when on scheduler tab and not impersonating */}
          {activeTab === 'scheduler' && !impersonatedInstructor && (
            <button style={BTN_SECONDARY} onClick={() => setShowConstraints(c => !c)}>
              {showConstraints ? '✖ Close Constraints' : '⚙️ Constraints'}
            </button>
          )}
        </div>
      </div>

      {/* Impersonation active banner */}
      {impersonatedInstructor && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.875rem 1.25rem',
          background: 'rgba(250,204,21,0.08)',
          border: '1px solid rgba(250,204,21,0.35)',
          borderRadius: '10px',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '20px' }}>👁️</span>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#facc15' }}>Impersonation Mode Active</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '1px' }}>
                Viewing as <strong style={{ color: 'var(--text-primary)' }}>{impersonatedInstructor.name}</strong>
                {' '}&mdash; {impersonatedInstructor.email}
              </div>
            </div>
          </div>
          <button
            onClick={exitImpersonation}
            style={{
              padding: '8px 18px',
              background: 'rgba(250,204,21,0.15)',
              border: '1px solid rgba(250,204,21,0.4)',
              borderRadius: '8px',
              color: '#facc15',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            ✖ Exit Impersonation Mode
          </button>
        </div>
      )}

      {/* While impersonating: show tabs (My Schedule / My Profile) */}
      {impersonatedInstructor ? (
        <div>
          {/* Impersonation sub-tab bar */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '1.5rem', borderBottom: '2px solid var(--card-border)' }}>
            {([{ id: 'schedule', label: '📋 My Schedule' }, { id: 'profile', label: '👤 My Profile' }] as const).map(t => (
              <button
                key={t.id}
                onClick={() => setImpersonationTab(t.id)}
                style={{
                  padding: '8px 20px',
                  border: 'none',
                  borderBottom: `3px solid ${impersonationTab === t.id ? 'var(--accent)' : 'transparent'}`,
                  background: 'transparent',
                  color: impersonationTab === t.id ? 'var(--accent)' : 'var(--text-secondary)',
                  fontSize: '14px',
                  fontWeight: impersonationTab === t.id ? '600' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  marginBottom: '-2px',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
          {impersonationTab === 'schedule' && <InstructorScheduleView asEmail={impersonatedInstructor.email} />}
          {impersonationTab === 'profile'  && <InstructorProfileView  asEmail={impersonatedInstructor.email} />}
        </div>
      ) : (
        <>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '1.5rem', borderBottom: '2px solid var(--card-border)', paddingBottom: '0' }}>
        {([
          { id: 'scheduler', label: '▶ Scheduler' },
          { id: 'data', label: '📋 Data Management' },
          { id: 'audit', label: '🕵️ Audit Log' },
          ...(isSuperAdmin ? [{ id: 'activity', label: '👥 Active Users' }] : []),
        ] as { id: 'scheduler' | 'data' | 'audit' | 'activity'; label: string }[]).map(tab => (
          <button
            key={tab.id}
            onClick={() => navigate(tab.id === 'scheduler' ? basePath : `${basePath}/${tab.id}`)}
            style={{
              padding: '8px 20px',
              border: 'none',
              borderBottom: `3px solid ${activeTab === tab.id ? 'var(--accent)' : 'transparent'}`,
              background: 'transparent',
              color: activeTab === tab.id ? 'var(--accent)' : 'var(--text-secondary)',
              fontSize: '14px',
              fontWeight: activeTab === tab.id ? '600' : '400',
              cursor: 'pointer',
              transition: 'all 0.15s',
              marginBottom: '-2px',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Data Management tab */}
      {activeTab === 'data' && <DataManagementPanel key={dataRefreshKey} />}

      {/* Audit Log tab */}
      {activeTab === 'audit' && (() => {
        const auditSubTab = location.pathname.startsWith(`${basePath}/audit/changes`) ? 'changes' : 'impersonation';
        return (
          <div style={CARD}>
            {/* Sub-tab bar */}
            <div style={{ display: 'flex', gap: '2px', marginBottom: '1.5rem', borderBottom: '1px solid var(--card-border)', paddingBottom: '0', flexWrap: 'wrap' }}>
              {([
                { id: 'impersonation', label: '🕵️ Impersonation Log', path: `${basePath}/audit` },
                { id: 'changes',       label: '📋 Admin Change Log',  path: `${basePath}/audit/changes` },
              ] as const).map(tab => (
                <button
                  key={tab.id}
                  onClick={() => navigate(tab.path)}
                  style={{
                    padding: '7px 14px',
                    border: 'none',
                    borderBottom: `3px solid ${auditSubTab === tab.id ? 'var(--accent)' : 'transparent'}`,
                    background: 'transparent',
                    color: auditSubTab === tab.id ? 'var(--accent)' : 'var(--text-secondary)',
                    fontSize: '13px',
                    fontWeight: auditSubTab === tab.id ? '600' : '400',
                    cursor: 'pointer',
                    marginBottom: '-1px',
                    transition: 'all 0.15s',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {auditSubTab === 'impersonation' && <ImpersonationAuditLog alwaysOpen />}
            {auditSubTab === 'changes' && <ChangeAuditLog alwaysOpen />}
          </div>
        );
      })()}

      {/* Active Users tab (super admin only) */}
      {activeTab === 'activity' && isSuperAdmin && <ActiveUsersPanel />}

      {/* Scheduler tab */}
      {activeTab === 'scheduler' && (
        <>

      {/* Inline Constraints Panel */}
      {showConstraints && (
        <div style={{ marginBottom: '1.5rem' }}>
          <ConstraintsEditor onClose={() => setShowConstraints(false)} />
        </div>
      )}

      {/* SMS Sync Panel */}
      <div style={{ ...CARD, marginBottom: '1.5rem', borderColor: 'rgba(99,102,241,0.4)', background: 'rgba(99,102,241,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '280px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.4rem' }}>
              <span style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>🔄 Step 1 — Sync Student Data from SMS</span>
            </div>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              <strong style={{ color: '#facc15' }}>This must be done before running the scheduler to ensure student enrollment data is current.</strong>
            </p>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {smsStatus?.sms_synced_at
                ? <>Last synced: <strong style={{ color: '#4ade80' }}>{new Date(smsStatus.sms_synced_at).toLocaleString()}</strong></>
                : <span style={{ color: '#f87171' }}>⚠️ Never synced — sync before running the scheduler.</span>
              }
              {/* Show sync duration permanently from persisted metadata, or from in-session state if just synced */}
              {(smsSyncDuration || smsStatus?.sync_duration_secs != null) && (
                <div style={{ marginTop: '4px' }}>
                  Sync duration: <strong style={{ color: '#4ade80' }}>
                    {smsSyncDuration ?? `${smsStatus!.sync_duration_secs}s`}
                  </strong>
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 }}>
            <button
              style={{
                ...BTN_PRIMARY,
                background: smsSyncing ? 'var(--bg-secondary)' : 'rgba(99,102,241,0.8)',
                color: smsSyncing ? '#facc15' : 'var(--button-text)',
                opacity: smsSyncing ? 0.85 : 1,
                minWidth: '180px',
              }}
              onClick={handleSmsSync}
              disabled={smsSyncing}
            >
              {smsSyncing ? `⏳ Syncing from SMS… ${smsElapsed}s` : '🔄 Sync from SMS'}
            </button>
            {/* Show live sync message first; fall back to persisted last-sync summary */}
            {smsSyncMsg ? (
              <span style={{ fontSize: '12px', color: smsSyncMsg.startsWith('✅') ? '#4ade80' : '#facc15', textAlign: 'right', maxWidth: '260px' }}>
                {smsSyncMsg}
              </span>
            ) : !smsSyncing && smsStatus?.sms_synced_at && smsStatus?.sync_duration_secs != null && (smsStatus.bachelor_rows != null || smsStatus.nevada_rows != null) && (
              <span style={{ fontSize: '12px', color: '#4ade80', textAlign: 'right', maxWidth: '260px' }}>
                ✅ Sync complete —{' '}
                {[
                  smsStatus.bachelor_rows != null && `Bachelor: ${smsStatus.bachelor_rows.toLocaleString()} rows`,
                  smsStatus.nevada_rows != null && `Nevada: ${smsStatus.nevada_rows.toLocaleString()} rows`,
                ].filter(Boolean).join(', ')}
                {' '}({smsStatus.sync_duration_secs}s)
              </span>
            )}
            {smsSyncError && (
              <span style={{ fontSize: '12px', color: '#f87171', textAlign: 'right', maxWidth: '260px' }}>
                {smsSyncError}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Instructor Sync Panel — Step 2 */}
      <div style={{ ...CARD, marginBottom: '1.5rem', borderColor: 'rgba(251,146,60,0.4)', background: 'rgba(251,146,60,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '280px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.4rem' }}>
              <span style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>👩‍🏫 Step 2 — Sync Instructor Data from SMS</span>
            </div>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              <strong style={{ color: '#fb923c' }}>Checks SMS for instructor data. If SMS has the required fields, syncs to facman2. Otherwise, uses existing facman2 data.</strong>
              {' '}Scheduling notes, preferences, and credentials are always preserved.
            </p>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {instrSyncStatus?.instructor_synced_at
                ? <>
                    Last checked: <strong style={{ color: '#4ade80' }}>{new Date(instrSyncStatus.instructor_synced_at).toLocaleString()}</strong>
                    {instrSyncStatus.source && (
                      <span style={{ marginLeft: '6px', color: instrSyncStatus.source === 'sms' ? '#4ade80' : '#fb923c' }}>
                        ({instrSyncStatus.source === 'sms' ? '📡 synced from SMS' : '📂 using facman2 data'})
                      </span>
                    )}
                  </>
                : <span style={{ color: '#94a3b8' }}>Not yet run — will check SMS first, then fall back to facman2 if needed.</span>
              }
              {instrSyncStatus?.sync_duration_secs != null && (
                <div style={{ marginTop: '4px' }}>
                  Last duration: <strong style={{ color: '#4ade80' }}>{instrSyncStatus.sync_duration_secs}s</strong>
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 }}>
            <button
              style={{
                ...BTN_PRIMARY,
                background: instrSyncing ? 'var(--bg-secondary)' : 'rgba(251,146,60,0.8)',
                color: instrSyncing ? '#fb923c' : 'var(--button-text)',
                opacity: instrSyncing ? 0.85 : 1,
                minWidth: '200px',
              }}
              onClick={handleInstructorSync}
              disabled={instrSyncing}
            >
              {instrSyncing ? `⏳ Checking instructors… ${instrElapsed}s` : '👩‍🏫 Check & Sync Instructors'}
            </button>
            {instrSyncMsg && (
              <span style={{ fontSize: '12px', color: '#4ade80', textAlign: 'right', maxWidth: '280px' }}>{instrSyncMsg}</span>
            )}
            {instrSyncError && (
              <span style={{ fontSize: '12px', color: '#f87171', textAlign: 'right', maxWidth: '280px' }}>{instrSyncError}</span>
            )}
            {!instrSyncing && !instrSyncMsg && instrSyncStatus?.instructor_synced_at && instrSyncStatus.rows_written != null && (
              <span style={{ fontSize: '12px', color: '#4ade80', textAlign: 'right', maxWidth: '280px' }}>
                ✅ {instrSyncStatus.rows_written} instructors updated ({instrSyncStatus.sync_duration_secs}s)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Run Panel */}
      <div style={{ ...CARD, marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 0.75rem 0', color: 'var(--text-primary)', fontSize: '16px' }}>▶ Step 3 — Run Scheduler</h3>
        {/* Semester disclaimer */}
        <div style={{
          padding: '10px 14px',
          background: 'rgba(250,204,21,0.08)',
          border: '1px solid rgba(250,204,21,0.3)',
          borderRadius: '8px',
          marginBottom: '1rem',
          fontSize: '13px',
          color: '#facc15',
          lineHeight: 1.5,
        }}>
          ⚠️ The scheduler is intended to be run <strong>once per semester</strong> under normal circumstances.
          You may run it multiple times with different constraints to compare options, then click
          <strong> Set as Live</strong> on the result you want instructors to see.
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Term Start</label>
            <input
              type="date"
              value={termStart}
              onChange={e => setTermStart(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '14px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Term End</label>
            <input
              type="date"
              value={termEnd}
              onChange={e => setTermEnd(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '14px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Term Name <span style={{ fontWeight: 400 }}>(optional)</span></label>
            <input
              type="text"
              placeholder="e.g. Spring 2026"
              value={termName}
              onChange={e => setTermName(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '14px', width: '160px' }}
            />
          </div>
          <button
            style={{ ...BTN_PRIMARY, opacity: running ? 0.6 : 1 }}
            onClick={handleRunScheduler}
            disabled={running}
          >
            {running ? '⏳ Running…' : '▶ Run Scheduler'}
          </button>
        </div>
        {runError && (
          <p style={{ margin: '0.75rem 0 0 0', color: '#f87171', fontSize: '13px' }}>❌ {runError}</p>
        )}
        {running && (
          <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {/* Elapsed timer */}
            <span style={{
              fontFamily: 'monospace',
              fontSize: '13px',
              fontWeight: '600',
              color: cancelling ? '#f87171' : '#facc15',
              background: cancelling ? 'rgba(248,113,113,0.1)' : 'rgba(250,204,21,0.1)',
              border: `1px solid ${cancelling ? 'rgba(248,113,113,0.3)' : 'rgba(250,204,21,0.3)'}`,
              borderRadius: '6px',
              padding: '3px 10px',
              minWidth: '64px',
              textAlign: 'center',
            }}>
              {String(Math.floor(elapsedSeconds / 60)).padStart(2,'0')}:{String(elapsedSeconds % 60).padStart(2,'0')}
            </span>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              {cancelling
                ? '🛑 Cancellation requested — waiting for current step to finish…'
                : '⏳ Scheduler is running in the background. Results will appear automatically…'}
            </span>
            {selectedRun && (() => {
              // Detect stuck run: running for > 15 minutes
              const startedAt = selectedRun.created_at ? new Date(selectedRun.created_at).getTime() : 0;
              const minutesRunning = startedAt ? (Date.now() - startedAt) / 60000 : 0;
              const isStuck = minutesRunning > 15 && selectedRun.status === 'running';
              return isStuck ? (
                <span style={{ fontSize: '12px', color: '#f87171', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: '4px', padding: '2px 8px' }}>
                  ⚠️ Run has been going for {Math.round(minutesRunning)}m — it may be stuck. Try stopping it.
                </span>
              ) : (
                <span style={{
                  fontSize: '11px',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  background: selectedRun.status === 'running' ? 'rgba(250,204,21,0.15)' : 'rgba(150,150,150,0.15)',
                  color: selectedRun.status === 'running' ? '#facc15' : 'var(--text-secondary)',
                }}>
                  {selectedRun.status}
                </span>
              );
            })()}
            <button
              onClick={handleCancelRun}
              disabled={cancelling}
              style={{
                padding: '5px 14px',
                background: cancelling ? 'rgba(150,150,150,0.2)' : 'rgba(239,68,68,0.15)',
                color: cancelling ? 'var(--text-secondary)' : '#f87171',
                border: `1px solid ${cancelling ? 'var(--card-border)' : '#f87171'}`,
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: cancelling ? 'not-allowed' : 'pointer',
              }}
            >
              {cancelling ? '🛑 Stopping…' : '⏹ Stop'}
            </button>
          </div>
        )}
      </div>



      {/* Run History + Stats */}
      {runs.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '1.5rem', marginBottom: '1.5rem', alignItems: 'stretch' }}>
          {/* Run selector */}
          <div style={{ ...CARD, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexShrink: 0 }}>
              <h3 style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)' }}>Runs</h3>
              <button
                onClick={() => loadRuns()}
                title="Refresh runs list"
                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '14px', cursor: 'pointer', padding: '2px 4px', lineHeight: 1 }}
              >
                🔄
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', overflowY: 'auto', flex: 1 }}>

              {/* ── Current Run (most recent) ── */}
              <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '0 2px 2px' }}>Current Run</div>
              {(() => {
                const run = runs[0];
                const isDeletable = (run.status === 'failed' || run.status === 'cancelled' || run.status === 'complete' || run.status === 'pending' || run.status === 'running') && !run.is_live;
                const isDeleting = deletingRunId === run.run_id;
                return (
                  <div
                    key={run.run_id}
                    onClick={() => selectRun(run)}
                    style={{
                      position: 'relative',
                      textAlign: 'left',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: `1px solid ${selectedRun?.run_id === run.run_id ? 'var(--accent)' : 'rgba(var(--accent-rgb,255,215,0),0.4)'}`,
                      background: selectedRun?.run_id === run.run_id ? 'rgba(var(--accent-rgb,255,215,0),0.08)' : 'rgba(var(--accent-rgb,255,215,0),0.03)',
                      cursor: 'pointer',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <div style={{ fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', paddingRight: isDeletable ? '22px' : '0' }}>
                      {run.is_live && (
                        <span style={{ fontSize: '10px', background: '#4ade80', color: '#000', borderRadius: '3px', padding: '1px 6px', fontWeight: '700', flexShrink: 0 }}>LIVE</span>
                      )}
                      {run.term_name ? (
                        <><span style={{ color: 'var(--accent)' }}>{run.term_name}</span> · {run.term_start} → {run.term_end}</>
                      ) : (
                        <>{run.term_start} → {run.term_end}</>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                      {run.status === 'complete'
                        ? `✅ ${run.stats?.total_sections ?? '—'} sections`
                        : run.status === 'failed' ? '❌ Failed'
                        : run.status === 'cancelled' ? '🛑 Cancelled'
                        : (() => {
                            const active = (run.progress_steps as any[])?.find(s => s.status === 'running');
                            const done = (run.progress_steps as any[])?.filter(s => s.status === 'done')?.length ?? 0;
                            return active ? `⏳ Step ${done + 1}/5: ${active.label}` : (() => {
                              const ageMs = run.created_at ? Date.now() - new Date(run.created_at).getTime() : 0;
                              const ageMin = Math.floor(ageMs / 60000);
                              return `⏳ Queued${ageMin > 0 ? ` (${ageMin}m ago — click × to remove)` : '…'}`;
                            })();
                          })()
                      }
                    </div>
                    {isDeletable && (
                      <span
                        title="Delete this run"
                        onClick={e => { e.stopPropagation(); handleDeleteRun(run.run_id); }}
                        style={{ position: 'absolute', top: '6px', right: '8px', fontSize: '13px', color: isDeleting ? 'var(--text-secondary)' : '#f87171', cursor: isDeleting ? 'not-allowed' : 'pointer', lineHeight: 1, padding: '2px 4px' }}
                      >
                        {isDeleting ? '…' : '×'}
                      </span>
                    )}
                  </div>
                );
              })()}

              {/* ── Previous Runs ── */}
              {runs.length > 1 && (
                <>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '6px 2px 2px', borderTop: '1px solid var(--card-border)', marginTop: '4px' }}>Previous Runs</div>
                  {(showAllRuns ? runs.slice(1) : runs.slice(1, 4)).map(run => {
                    const isDeletable = (run.status === 'failed' || run.status === 'cancelled' || run.status === 'complete' || run.status === 'pending' || run.status === 'running') && !run.is_live;
                    const isDeleting = deletingRunId === run.run_id;
                    return (
                      <div
                        key={run.run_id}
                        onClick={() => selectRun(run)}
                        style={{
                          position: 'relative',
                          textAlign: 'left',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: `1px solid ${selectedRun?.run_id === run.run_id ? 'var(--accent)' : 'var(--card-border)'}`,
                          background: selectedRun?.run_id === run.run_id ? 'rgba(var(--accent-rgb,255,215,0),0.08)' : 'transparent',
                          cursor: 'pointer',
                          color: 'var(--text-primary)',
                        }}
                      >
                        <div style={{ fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', paddingRight: isDeletable ? '22px' : '0' }}>
                          {run.is_live && (
                            <span style={{ fontSize: '10px', background: '#4ade80', color: '#000', borderRadius: '3px', padding: '1px 6px', fontWeight: '700', flexShrink: 0 }}>LIVE</span>
                          )}
                          {run.term_name ? (
                            <><span style={{ color: 'var(--accent)' }}>{run.term_name}</span> · {run.term_start} → {run.term_end}</>
                          ) : (
                            <>{run.term_start} → {run.term_end}</>
                          )}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                          {run.status === 'complete'
                            ? `✅ ${run.stats?.total_sections ?? '—'} sections`
                            : run.status === 'failed' ? '❌ Failed'
                            : run.status === 'cancelled' ? '🛑 Cancelled'
                            : (() => {
                                const active = (run.progress_steps as any[])?.find(s => s.status === 'running');
                                const done = (run.progress_steps as any[])?.filter(s => s.status === 'done')?.length ?? 0;
                                return active ? `⏳ Step ${done + 1}/5: ${active.label}` : (() => {
                                  const ageMs = run.created_at ? Date.now() - new Date(run.created_at).getTime() : 0;
                                  const ageMin = Math.floor(ageMs / 60000);
                                  return `⏳ Queued${ageMin > 0 ? ` (${ageMin}m ago — click × to remove)` : '…'}`;
                                })();
                              })()
                          }
                        </div>
                        {isDeletable && (
                          <span
                            title="Delete this run"
                            onClick={e => { e.stopPropagation(); handleDeleteRun(run.run_id); }}
                            style={{ position: 'absolute', top: '6px', right: '8px', fontSize: '13px', color: isDeleting ? 'var(--text-secondary)' : '#f87171', cursor: isDeleting ? 'not-allowed' : 'pointer', lineHeight: 1, padding: '2px 4px' }}
                          >
                            {isDeleting ? '…' : '×'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {runs.length > 4 && (
                    <button
                      onClick={() => setShowAllRuns(v => !v)}
                      style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '11px', cursor: 'pointer', padding: '4px 0', textAlign: 'left', flexShrink: 0 }}
                    >
                      {showAllRuns ? '▲ Show less' : `▼ ${runs.length - 4} older run${runs.length - 4 === 1 ? '' : 's'}…`}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Right column: progress tracker (in-progress) and/or stats (complete) */}
          {(selectedRun || loadingSelected) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Progress steps panel */}
              {selectedRun && (selectedRun.status === 'pending' || selectedRun.status === 'running') && (
                <div style={{ ...CARD, flexShrink: 0 }}>
                  <h3 style={{ margin: '0 0 1rem 0', fontSize: '16px', color: 'var(--text-primary)' }}>⏳ Scheduler Running</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {((selectedRun.progress_steps as any[]) ?? []).map((step: any) => (
                      <div
                        key={step.step}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: '12px',
                          padding: '10px 14px', borderRadius: '8px',
                          background: step.status === 'running' ? 'rgba(250,204,21,0.06)'
                                    : step.status === 'done'    ? 'rgba(74,222,128,0.05)' : 'transparent',
                          border: `1px solid ${step.status === 'running' ? 'rgba(250,204,21,0.3)'
                                             : step.status === 'done'    ? 'rgba(74,222,128,0.2)'
                                             : 'var(--card-border)'}`,
                        }}
                      >
                        <span style={{ fontSize: '16px', lineHeight: '1.2', minWidth: '20px' }}>
                          {step.status === 'done' ? '✅' : step.status === 'running' ? '⏳' : step.status === 'failed' ? '❌' : '○'}
                        </span>
                        <div>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: step.status === 'running' ? '600' : '400',
                            color: step.status === 'pending' ? 'var(--text-secondary)' : 'var(--text-primary)',
                          }}>
                            {step.step}. {step.label}
                          </div>
                          {step.detail && (
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>{step.detail}</div>
                          )}
                        </div>
                      </div>
                    ))}
                    {(!selectedRun.progress_steps || selectedRun.progress_steps.length === 0) && (
                      <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: 0 }}>Initializing…</p>
                    )}
                  </div>
                </div>
              )}

              {/* Stats card */}
              {selectedRun?.stats && (
                <div style={{ ...CARD, flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--text-primary)' }}>
                      Results: {selectedRun.term_name ? `${selectedRun.term_name} · ` : ''}{selectedRun.term_start} – {selectedRun.term_end}
                    </h3>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      {selectedRun.is_live ? (
                        <>
                          <span style={{ fontSize: '12px', background: '#4ade80', color: '#000', padding: '4px 10px', borderRadius: '4px', fontWeight: '700' }}>LIVE</span>
                          <button
                            style={{ ...BTN_SECONDARY, fontSize: '13px', padding: '7px 14px', opacity: settingLive ? 0.6 : 1, color: '#f87171', borderColor: '#f87171' }}
                            onClick={handleUnsetLive}
                            disabled={settingLive}
                          >
                            {settingLive ? '⏳ Updating…' : '🔴 Remove Live'}
                          </button>
                        </>
                      ) : (
                        <button
                          style={{ ...BTN_SECONDARY, fontSize: '13px', padding: '7px 14px', opacity: settingLive ? 0.6 : 1 }}
                          onClick={handleSetLive}
                          disabled={settingLive}
                        >
                          {settingLive ? '⏳ Setting…' : '🟢 Set as Live'}
                        </button>
                      )}
                      <button style={BTN_SECONDARY} onClick={() => exportCsv(selectedRun.sections)}>⬇️ Export CSV</button>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '1rem' }}>
                    {[
                      { label: 'Sections', value: selectedRun.stats.total_sections, tooltip: 'Total sections in this run (includes unstaffed)' },
                      { label: 'Students Placed', value: uniqueStudentCount || selectedRun.stats.total_registrations, tooltip: 'Unique students assigned to at least one section' },
                      { label: 'Time Match', value: `${selectedRun.stats.time_match_pct}%`, tooltip: 'Students placed in their preferred time slot' },
                      { label: 'Unstaffed Sections', value: effectiveUnstaffedCount, warn: effectiveUnstaffedCount > 0, tooltip: 'Sections with enrolled students but no instructor assigned (empty unstaffed sections excluded)' },
                      { label: 'In Unstaffed', value: studentsInUnstaffed, warn: studentsInUnstaffed > 0, tooltip: 'Unique students enrolled in sections that have no instructor assigned' },
                    ].map(stat => (
                      <div key={stat.label} title={stat.tooltip} style={{ background: 'var(--bg-secondary)', borderRadius: '8px', padding: '1rem', textAlign: 'center', cursor: stat.tooltip ? 'help' : 'default' }}>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: stat.warn ? '#f87171' : 'var(--accent)' }}>{stat.value}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{stat.label}</div>
                      </div>
                    ))}
                  </div>
                  {selectedRun.stats.skipped_courses?.length > 0 && (
                    <div style={{ marginTop: '0.75rem', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      ⚠️ Skipped (no credentialed instructor): {selectedRun.stats.skipped_courses.join(', ')}
                    </div>
                  )}
                  {/* Panel toggle buttons */}
                  <div style={{ borderTop: '1px solid var(--card-border)', marginTop: '1rem', paddingTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => setShowSections(v => !v)}
                      style={{ ...BTN_SECONDARY, fontSize: '13px', padding: '7px 14px', display: 'inline-flex', alignItems: 'center', gap: '6px', background: showSections ? 'rgba(var(--accent-rgb,255,215,0),0.08)' : 'var(--bg-secondary)', borderColor: showSections ? 'var(--accent)' : 'var(--card-border)', color: showSections ? 'var(--accent)' : 'var(--text-primary)' }}
                    >
                      📋 Sections ({filteredSections.length}) {showSections ? '▲' : '▼'}
                    </button>
                    <button
                      onClick={() => setShowInstructorMatrix(v => !v)}
                      style={{ ...BTN_SECONDARY, fontSize: '13px', padding: '7px 14px', display: 'inline-flex', alignItems: 'center', gap: '6px', background: showInstructorMatrix ? 'rgba(var(--accent-rgb,255,215,0),0.08)' : 'var(--bg-secondary)', borderColor: showInstructorMatrix ? 'var(--accent)' : 'var(--card-border)', color: showInstructorMatrix ? 'var(--accent)' : 'var(--text-primary)' }}
                    >
                      📊 Instructor Matrix {showInstructorMatrix ? '▲' : '▼'}
                    </button>
                    <button
                      onClick={() => setShowStudentMatrix(v => !v)}
                      style={{ ...BTN_SECONDARY, fontSize: '13px', padding: '7px 14px', display: 'inline-flex', alignItems: 'center', gap: '6px', background: showStudentMatrix ? 'rgba(var(--accent-rgb,255,215,0),0.08)' : 'var(--bg-secondary)', borderColor: showStudentMatrix ? 'var(--accent)' : 'var(--card-border)', color: showStudentMatrix ? 'var(--accent)' : 'var(--text-primary)' }}
                    >
                      🎓 Student Matrix {showStudentMatrix ? '▲' : '▼'}
                    </button>
                  </div>
                </div>
              )}

              {loadingSelected && (
                <div style={{ ...CARD, flex: 1, textAlign: 'center', color: 'var(--text-secondary)', padding: '3rem' }}>Loading run details…</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Sections Table */}
      {selectedRun && showSections && (
        <div style={CARD}>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--text-primary)' }}>
              Sections ({filteredSections.length})
            </h3>
            <input
              placeholder="Filter by course…"
              value={filterCourse}
              onChange={e => setFilterCourse(e.target.value)}
              style={{ padding: '7px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '13px', width: '160px' }}
            />
            <input
              placeholder="Filter by instructor…"
              value={filterInstructor}
              onChange={e => setFilterInstructor(e.target.value)}
              style={{ padding: '7px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '13px', width: '200px' }}
            />
            <button
              onClick={() => { setShowCreateSection(true); setCreateSectionError(null); }}
              style={{ ...BTN_SECONDARY, fontSize: '13px', padding: '7px 14px', marginLeft: 'auto' }}
            >
              ＋ New Section
            </button>
          </div>

          {filteredSections.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem 0' }}>
              {loadingRuns || loadingSelected ? 'Loading…' : 'No sections to display.'}
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--card-border)' }}>
                    <th style={{ padding: '10px 6px', width: '28px' }} />
                    {['Course', 'Instructor', 'Time Slot', 'Enrolled / Cap', 'Status', ''].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: '600', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredSections.map((s, i) => {
                    const isExpanded = expandedSectionIds.has(s.section_id);
                    const hasStudents = s.students && s.students.length > 0;
                    const rowBg = i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)';
                    return (
                      <>
                        <tr key={s.section_id} style={{ borderBottom: isExpanded ? 'none' : '1px solid var(--card-border)', background: rowBg }}>
                          <td style={{ padding: '10px 6px', textAlign: 'center' }}>
                            {hasStudents && (
                              <button
                                onClick={() => toggleSection(s.section_id)}
                                title={isExpanded ? 'Hide students' : 'Show students'}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '12px', padding: '0 2px', lineHeight: 1 }}
                              >
                                {isExpanded ? '▾' : '▸'}
                              </button>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px', fontWeight: '600', color: 'var(--text-primary)' }}>{s.course.toUpperCase()}</td>
                          <td style={{ padding: '10px 12px', color: s.instructor ? 'var(--text-primary)' : '#f87171' }}>
                            {s.instructor ?? '⚠️ Unstaffed'}
                          </td>
                          <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>{getSlotLabel(s.slot)}</td>
                          <td style={{ padding: '10px 12px', color: 'var(--text-primary)' }}>
                            <span style={{ color: s.enrolled >= s.capacity ? '#f87171' : 'var(--accent)' }}>{s.enrolled}</span>
                            <span style={{ color: 'var(--text-secondary)' }}> / {s.capacity}</span>
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            {s.instructor
                              ? <span style={{ color: '#4ade80', fontSize: '12px' }}>✅ Staffed</span>
                              : <span style={{ color: '#f87171', fontSize: '12px' }}>❌ Unstaffed</span>}
                          </td>
                          <td style={{ padding: '10px 6px', textAlign: 'center' }}>
                            <button
                              onClick={() => { setDeletingSectionTarget(s); setDeleteSectionError(null); setDeleteSectionForce(false); }}
                              title="Delete this section"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '14px', padding: '0 4px', lineHeight: 1, opacity: 0.6 }}
                              onMouseEnter={e => (e.currentTarget.style.color = '#f87171', e.currentTarget.style.opacity = '1')}
                              onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-secondary)', e.currentTarget.style.opacity = '0.6')}
                            >
                              🗑
                            </button>
                          </td>
                        </tr>
                        {isExpanded && hasStudents && (
                          <tr key={`${s.section_id}-students`} style={{ borderBottom: '1px solid var(--card-border)', background: rowBg }}>
                            <td />
                            <td colSpan={5} style={{ padding: '0 12px 10px 12px' }}>
                              <div style={{ background: 'var(--bg-secondary)', borderRadius: '6px', padding: '8px 12px', fontSize: '12px' }}>
                                <div style={{ color: 'var(--text-secondary)', fontWeight: '600', marginBottom: '6px' }}>
                                  Students ({s.students!.length})
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                  {s.students!.map((st, si) => (
                                    <span
                                      key={`${st.StudentNo}-${st.DB}-${si}`}
                                      style={{
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        background: 'var(--card-bg)',
                                        border: '1px solid var(--card-border)',
                                        color: 'var(--text-primary)',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {st.StudentNo}
                                      <span style={{ color: 'var(--text-secondary)', marginLeft: '4px', fontSize: '11px' }}>{st.DB}</span>
                                      {st.preferred_slot && st.preferred_slot !== s.slot && (
                                        <span title="Placed in non-preferred slot" style={{ color: '#f87171', marginLeft: '4px', fontSize: '11px' }}>⚠</span>
                                      )}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Instructor Matrix */}
      {selectedRun && showInstructorMatrix && selectedRun.sections?.length > 0 && (() => {
        const SLOTS = ['1','2','3','4','5'];
        const SLOT_STARTS: Record<string,string> = {
          '1':'8:00 AM','2':'11:00 AM','3':'2:00 PM','4':'5:00 PM','5':'7:35 PM'
        };

        const byInstructor: Record<string, SchedulerSection[]> = {};
        const unstaffed: SchedulerSection[] = [];
        for (const s of selectedRun.sections) {
          if (!s.instructor) { unstaffed.push(s); continue; }
          if (!byInstructor[s.instructor]) byInstructor[s.instructor] = [];
          byInstructor[s.instructor].push(s);
        }
        let instructors = Object.keys(byInstructor).sort();
        // Only show slots that have at least one section
        const activeSlots = SLOTS.filter(sl =>
          selectedRun.sections.some(s => s.slot === sl)
        );

        // Filter: hide instructors with no sections in any active slot when toggle is on
        if (hideEmptyInstructorRows) {
          instructors = instructors.filter(instr =>
            activeSlots.some(sl => byInstructor[instr].some(s => s.slot === sl))
          );
        }
        // Filter: hide instructors whose sections have no enrolled students
        if (hideNoStudentInstructors) {
          instructors = instructors.filter(instr =>
            byInstructor[instr].some(s => s.students && s.students.length > 0)
          );
        }

        return (
          <div style={{ ...CARD, marginTop: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--text-primary)' }}>
                📊 Instructor Matrix
                <span style={{ fontSize: '13px', fontWeight: '400', color: 'var(--text-secondary)', marginLeft: '8px' }}>
                  ({instructors.length} of {Object.keys(byInstructor).length} instructors)
                </span>
              </h3>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={hideEmptyInstructorRows}
                    onChange={e => setHideEmptyInstructorRows(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  Hide instructors with no assigned sections
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={hideNoStudentInstructors}
                    onChange={e => setHideNoStudentInstructors(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  Hide instructors with no students in course
                </label>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <DndContext
                onDragStart={({ active }) => {
                  const sec = selectedRun.sections.find(s => s.section_id === active.id);
                  setActiveDragSection(sec ?? null);
                }}
                onDragEnd={handleSectionDragEnd}
                onDragCancel={() => setActiveDragSection(null)}
              >
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--card-border)' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: '600', whiteSpace: 'nowrap', minWidth: '200px' }}>
                      Instructor
                    </th>
                    {activeSlots.map(sl => (
                      <th key={sl} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: '600', whiteSpace: 'nowrap' }}>
                        {SLOT_STARTS[sl]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {instructors.map((instr, i) => {
                    const instrInfo = instructorLookup[instr.toLowerCase()];
                    const displayName = instrInfo?.name || instr;
                    const primaryArea = instrInfo?.primary_area;
                    const facultyType = instrInfo?.faculty_type;
                    // Read the caps the run was actually given rather than assuming them. This
                    // badge previously hardcoded 3/2 while the backend ran FT=4/Adjunct=3, so it
                    // reported "4/3 overload" on schedules that contained no violation at all.
                    // Runs created before constraints_used was recorded fall back to policy.
                    const maxSections = facultyType === 'FT'
                      ? (selectedRun?.constraints_used?.ft_max_sections ?? 3)
                      : (selectedRun?.constraints_used?.adjunct_max_sections ?? 2);
                    const sectionCount = byInstructor[instr].length;
                    const isOverloaded = sectionCount > maxSections;
                    const instrCredentials = credentialLookup[instr.toLowerCase()];
                    return (
                    <tr key={instr} style={{ borderBottom: '1px solid var(--card-border)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                      <td style={{ padding: '8px 12px', color: 'var(--text-primary)', fontWeight: '500', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <span>{displayName}</span>
                          {isOverloaded && (
                            <span
                              title={`${sectionCount} sections assigned — ${facultyType || 'Adjunct'} max is ${maxSections}`}
                              style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '3px', background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171', fontWeight: '700', whiteSpace: 'nowrap' }}
                            >
                              ⚠ {sectionCount}/{maxSections} overload
                            </span>
                          )}
                        </div>
                        {primaryArea && (
                          <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '3px', background: 'rgba(var(--accent-rgb,255,215,0),0.12)', border: '1px solid rgba(var(--accent-rgb,255,215,0),0.2)', color: 'var(--text-secondary)', fontWeight: '600', display: 'inline-block', marginTop: '2px' }}>
                            {primaryArea}
                          </span>
                        )}
                      </td>
                      {activeSlots.map(sl => {
                        const cell = byInstructor[instr].filter(s => s.slot === sl);
                        const dropId = `${instr}||${sl}`;
                        return (
                          <DroppableCell key={sl} id={dropId}>
                            {cell.length === 0 ? (
                              <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>—</span>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                {cell.map(s => {
                                  // Check credentials: if we have data and instructor is not credentialed for this course, flag it
                                  const isUnauthorized = instrCredentials !== undefined && !instrCredentials.has(s.course.toLowerCase());
                                  return (
                                  <DraggableSectionChip key={s.section_id} section={s}>
                                    {s.course.toUpperCase()}
                                    {isUnauthorized && (
                                      <span
                                        title={`${displayName} may not be credentialed to teach ${s.course.toUpperCase()}`}
                                        style={{ color: '#f87171', marginLeft: '4px', fontWeight: '700' }}
                                      >⚠</span>
                                    )}
                                    <span style={{ color: 'var(--text-secondary)', fontWeight: '400', marginLeft: '5px' }}>
                                      ({s.enrolled}/{s.capacity})
                                    </span>
                                  </DraggableSectionChip>
                                  );
                                })}
                              </div>
                            )}
                          </DroppableCell>
                        );
                      })}
                    </tr>
                    );
                  })}
                  {unstaffed.filter(s => (s.enrolled ?? 0) > 0).map((s, i) => (
                    <tr key={s.section_id} style={{ borderBottom: '1px solid var(--card-border)', background: 'rgba(248,113,113,0.04)' }}>
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                        {assigningInstructorToSection === s.section_id ? (
                          <select
                            autoFocus
                            style={{ fontSize: '12px', padding: '3px 6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--card-border)', borderRadius: '4px', cursor: 'pointer', minWidth: '160px' }}
                            value=""
                            onChange={e => { if (e.target.value) handleAssignInstructorToSection(s, e.target.value); }}
                            onBlur={() => setAssigningInstructorToSection(null)}
                          >
                            <option value="">Select instructor…</option>
                            {[...instructorList].sort((a, b) => a.name.localeCompare(b.name)).map(instr => (
                              <option key={instr.row_key} value={instr.email}>{instr.name}</option>
                            ))}
                          </select>
                        ) : (
                          <button
                            onClick={() => setAssigningInstructorToSection(s.section_id)}
                            style={{ color: '#f87171', fontStyle: 'italic', background: 'none', border: '1px dashed rgba(248,113,113,0.4)', borderRadius: '4px', cursor: 'pointer', padding: '2px 8px', fontSize: '11px' }}
                            title="Click to assign an instructor"
                          >
                            ⚠️ Unstaffed
                          </button>
                        )}
                      </td>
                      {activeSlots.map(sl => (
                        <td key={sl} style={{ padding: '8px 12px' }}>
                          {s.slot === sl ? (
                            <span style={{
                              padding: '2px 7px',
                              borderRadius: '4px',
                              background: 'rgba(248,113,113,0.1)',
                              border: '1px solid rgba(248,113,113,0.3)',
                              color: '#f87171',
                              fontWeight: '600',
                              whiteSpace: 'nowrap',
                            }}>
                              {s.course.toUpperCase()}
                              <span style={{ fontWeight: '400', marginLeft: '5px' }}>({s.enrolled}/{s.capacity})</span>
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>—</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {movingSectionPending && (
                <div style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontSize: '13px' }}>
                  ↕ Moving section…
                </div>
              )}
              {matrixEditError && (
                <div style={{ padding: '8px 12px', color: '#f87171', fontSize: '13px' }}>
                  ⚠ {matrixEditError}
                </div>
              )}
              <DragOverlay>
                {activeDragSection && (
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '4px',
                    background: 'var(--accent)',
                    color: 'var(--button-text)',
                    fontWeight: '700',
                    fontSize: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    cursor: 'grabbing',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                  }}>
                    {activeDragSection.course.toUpperCase()} ↕
                  </span>
                )}
              </DragOverlay>
              </DndContext>
            </div>
            {excelUrlsByRun[selectedRun.run_id] && (
              <div style={{ marginTop: '0.75rem', fontSize: '13px' }}>
                <a href={excelUrlsByRun[selectedRun.run_id]} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: '600' }}>
                  ⬇ Download updated Excel
                </a>
              </div>
            )}
          </div>
        );
      })()}

      {/* Student Matrix */}
      {selectedRun && showStudentMatrix && selectedRun.sections?.length > 0 && (() => {
        const SLOTS = ['1','2','3','4','5'];
        const SLOT_STARTS: Record<string,string> = {
          '1':'8:00 AM','2':'11:00 AM','3':'2:00 PM','4':'5:00 PM','5':'7:35 PM'
        };

        // SMS link helper — URL differs by database
        const getSmsUrl = (studentNo: string, db: string) => {
          const subdomain = db?.toLowerCase().includes('nevada') ? 'smsnevada' : 'smsbachelor';
          return `https://${subdomain}.lauruscollege.edu/ViewStudent.aspx?StudentNo=${encodeURIComponent(studentNo)}`;
        };

        // Build map: "StudentNo||DB" → { slot → { course, instructor, preferred_slot, section_id } }
        // Also build name lookup: StudentNo → { first_name, last_name }
        // And db_list lookup: StudentNo → "Bachelor" | "Nevada" | "Bachelor, Nevada"
        type StudentEntry = { course: string; instructor: string | null; preferred_slot: string | null; section_id: string };
        const studentMap: Record<string, Record<string, StudentEntry>> = {};
        const studentNameMap: Record<string, { first_name?: string; last_name?: string }> = {};
        const studentDbListMap: Record<string, string> = {};
        for (const s of selectedRun.sections) {
          if (!s.students?.length) continue;
          for (const st of s.students) {
            const key = `${st.StudentNo}||${st.DB}`;
            if (!studentMap[key]) studentMap[key] = {};
            studentMap[key][s.slot ?? 'none'] = {
              course: s.course,
              instructor: s.instructor,
              preferred_slot: st.preferred_slot ?? null,
              section_id: s.section_id,
            };
            if (!studentNameMap[st.StudentNo] || (!studentNameMap[st.StudentNo].first_name && !studentNameMap[st.StudentNo].last_name)) {
              if (st.first_name || st.last_name) {
                studentNameMap[st.StudentNo] = { first_name: st.first_name, last_name: st.last_name };
              }
            }
            if (!studentDbListMap[st.StudentNo] && (st as any).db_list) {
              studentDbListMap[st.StudentNo] = (st as any).db_list;
            }
          }
        }

        const activeSlots = SLOTS.filter(sl =>
          selectedRun.sections.some(s => s.slot === sl)
        );

        const allKeys = Object.keys(studentMap).sort();
        const filteredKeys = filterStudent.trim()
          ? allKeys.filter(k => {
              const [sno, db] = k.split('||');
              const lower = filterStudent.trim().toLowerCase();
              const nm = studentNameMap[sno] ?? {};
              return k.toLowerCase().includes(lower)
                || (nm.first_name ?? '').toLowerCase().includes(lower)
                || (nm.last_name ?? '').toLowerCase().includes(lower);
            })
          : allKeys;

        // Sort by selected column
        const sortedKeys = [...filteredKeys].sort((a, b) => {
          const [snoA, dbA] = a.split('||');
          const [snoB, dbB] = b.split('||');
          let cmp = 0;
          if (matrixSort.col === 'student') {
            // Numeric sort for student numbers
            const nA = parseFloat(snoA) || 0;
            const nB = parseFloat(snoB) || 0;
            cmp = nA !== nB ? nA - nB : snoA.localeCompare(snoB);
          } else if (matrixSort.col === 'name') {
            const nmA = studentNameMap[snoA] ?? {};
            const nmB = studentNameMap[snoB] ?? {};
            const nameA = `${nmA.last_name ?? ''}${nmA.first_name ?? ''}`.toLowerCase();
            const nameB = `${nmB.last_name ?? ''}${nmB.first_name ?? ''}`.toLowerCase();
            cmp = nameA.localeCompare(nameB);
          } else if (matrixSort.col === 'db') {
            const dbListA = (studentDbListMap[snoA] || dbA).toLowerCase();
            const dbListB = (studentDbListMap[snoB] || dbB).toLowerCase();
            cmp = dbListA.localeCompare(dbListB);
          }
          return matrixSort.dir === 'asc' ? cmp : -cmp;
        });

        const toggleSort = (col: 'student' | 'name' | 'db') => {
          setMatrixSort(prev => prev.col === col
            ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            : { col, dir: 'asc' }
          );
        };

        const sortArrow = (col: 'student' | 'name' | 'db') =>
          matrixSort.col !== col ? ' ⇅' : matrixSort.dir === 'asc' ? ' ↑' : ' ↓';

        const unmatched = selectedRun.unmatched_students ?? [];
        const filteredUnmatched = filterStudent.trim()
          ? unmatched.filter(u => {
              const lower = filterStudent.trim().toLowerCase();
              return u.StudentNo.toLowerCase().includes(lower)
                || u.DB.toLowerCase().includes(lower)
                || (u.first_name ?? '').toLowerCase().includes(lower)
                || (u.last_name ?? '').toLowerCase().includes(lower);
            })
          : unmatched;

        return (
          <div style={{ ...CARD, marginTop: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--text-primary)' }}>
                🎓 Student Matrix
                <span style={{ fontSize: '13px', fontWeight: '400', color: 'var(--text-secondary)', marginLeft: '8px' }}>
                  ({allKeys.length} student{allKeys.length !== 1 ? 's' : ''} assigned{unmatched.length > 0 ? ` · ` : ''}{unmatched.length > 0 && <span style={{ color: '#f87171' }} title="Students who need a course but could not be placed into any available section — either all sections were at capacity, or no section time matched their availability.">{unmatched.length} course conflict{unmatched.length !== 1 ? 's' : ''} ⓘ</span>})
                </span>
              </h3>
              <input
                placeholder="Search by student # or DB…"
                value={filterStudent}
                onChange={e => setFilterStudent(e.target.value)}
                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '13px', width: '220px' }}
              />
            </div>
            {/* Color legend */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem', fontSize: '11px' }}>
              <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Legend:</span>
              <span style={{ padding: '2px 8px', borderRadius: '4px', background: 'rgba(var(--accent-rgb,255,215,0),0.1)', border: '1px solid rgba(var(--accent-rgb,255,215,0),0.25)', color: 'var(--accent)', fontWeight: '600' }}>Normal</span>
              <span style={{ padding: '2px 8px', borderRadius: '4px', background: 'rgba(251,146,60,0.12)', border: '1px solid rgba(251,146,60,0.35)', color: '#fb923c', fontWeight: '600' }}>No instructor assigned</span>
              <span style={{ padding: '2px 8px', borderRadius: '4px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171', fontWeight: '600' }}>⚠ Not in preferred slot</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--card-border)' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: '600', whiteSpace: 'nowrap', minWidth: '120px' }}>
                        <button onClick={() => toggleSort('student')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontWeight: '600', fontSize: '12px', padding: 0 }}>Student #{sortArrow('student')}</button>
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: '600', whiteSpace: 'nowrap' }}>
                        <button onClick={() => toggleSort('name')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontWeight: '600', fontSize: '12px', padding: 0 }}>Name{sortArrow('name')}</button>
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: '600', whiteSpace: 'nowrap' }}>
                        <button onClick={() => toggleSort('db')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontWeight: '600', fontSize: '12px', padding: 0 }}>DB{sortArrow('db')}</button>
                      </th>
                      {activeSlots.map(sl => (
                        <th key={sl} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: '600', whiteSpace: 'nowrap' }}>
                          {SLOT_STARTS[sl]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedKeys.length === 0 ? (
                      <tr>
                        <td colSpan={3 + activeSlots.length} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                          No students match "{filterStudent}"
                        </td>
                      </tr>
                    ) : sortedKeys.map((key, i) => {
                      const [studentNo, db] = key.split('||');
                      const slotMap = studentMap[key];
                      const nm = studentNameMap[studentNo] ?? {};
                      const fullName = nm.last_name && nm.first_name
                        ? `${nm.last_name}, ${nm.first_name}`
                        : nm.last_name || nm.first_name || '';
                      return (
                        <tr key={key} style={{ borderBottom: '1px solid var(--card-border)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                          <td style={{ padding: '8px 12px', color: 'var(--text-primary)', fontWeight: '500', whiteSpace: 'nowrap' }}>
                            <a
                              href={getSmsUrl(studentNo, db)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: '600' }}
                              title="Open in SMS"
                            >
                              {studentNo} ↗
                            </a>
                          </td>
                          <td style={{ padding: '8px 12px', color: 'var(--text-primary)', whiteSpace: 'nowrap', minWidth: '130px' }}>
                            {fullName || <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                          </td>
                          <td style={{ padding: '8px 12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{studentDbListMap[studentNo] || db}</td>
                          {activeSlots.map(sl => {
                            const entry = slotMap[sl];
                            const isUnstaffed = entry && !entry.instructor;
                            const mismatched = entry && !isUnstaffed && entry.preferred_slot !== null && entry.preferred_slot !== sl;
                            const bgColor = isUnstaffed ? 'rgba(251,146,60,0.12)' : mismatched ? 'rgba(248,113,113,0.1)' : 'rgba(var(--accent-rgb,255,215,0),0.1)';
                            const borderColor = isUnstaffed ? 'rgba(251,146,60,0.35)' : mismatched ? 'rgba(248,113,113,0.3)' : 'rgba(var(--accent-rgb,255,215,0),0.25)';
                            const textColor = isUnstaffed ? '#fb923c' : mismatched ? '#f87171' : 'var(--accent)';
                            return (
                              <td key={sl} style={{ padding: '8px 12px', verticalAlign: 'top' }}>
                                {entry ? (
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{
                                      padding: '2px 7px',
                                      borderRadius: '4px',
                                      background: bgColor,
                                      border: `1px solid ${borderColor}`,
                                      color: textColor,
                                      fontWeight: '600',
                                      whiteSpace: 'nowrap',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                    }}>
                                      {entry.course.toUpperCase()}
                                      {isUnstaffed && <span title="No instructor assigned to this section">⚠</span>}
                                      {mismatched && <span title="Not in preferred slot">⚠</span>}
                                    </span>
                                    <button
                                      title="Move student to a different section or course"
                                      onClick={() => {
                                        setMovingStudent({ StudentNo: studentNo, DB: db, from_section_id: entry.section_id, course: entry.course, first_name: nm.first_name, last_name: nm.last_name });
                                        setStudentMoveTarget('');
                                        setMoveCrossCourse(false);
                                        setForceOverCapacity(false);
                                        setMatrixEditError(null);
                                      }}
                                      style={{ background: 'none', border: 'none', padding: '0 2px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '11px', lineHeight: 1 }}
                                    >
                                      ↕
                                    </button>
                                  </div>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>—</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
            </div>

            {/* Unregistered students panel */}
            {filteredUnmatched.length > 0 && (
              <div style={{ marginTop: '1.25rem', padding: '1rem 1.25rem', background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.75rem' }}>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#f87171' }}>
                    ⚠️ {filteredUnmatched.length} Course{filteredUnmatched.length !== 1 ? 's' : ''} Could Not Be Assigned
                  </span>
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    — all sections for their course were at capacity, or no time slot matched their availability
                  </span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(248,113,113,0.2)' }}>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: '#f87171', fontWeight: '600', whiteSpace: 'nowrap' }}>Student #</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: '#f87171', fontWeight: '600' }}>Name</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: '#f87171', fontWeight: '600' }}>DB</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: '#f87171', fontWeight: '600' }}>Course Needed</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: '#f87171', fontWeight: '600' }}>SMS Profile</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: '#f87171', fontWeight: '600' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUnmatched.map((u, i) => {
                        const unmatchedName = u.last_name && u.first_name ? `${u.last_name}, ${u.first_name}` : (u.last_name || u.first_name || '');
                        return (
                        <tr key={`${u.StudentNo}||${u.DB}`} style={{ borderBottom: '1px solid rgba(248,113,113,0.1)', background: i % 2 === 0 ? 'transparent' : 'rgba(248,113,113,0.03)' }}>
                          <td style={{ padding: '6px 10px', color: 'var(--text-primary)', fontWeight: '500' }}>{u.StudentNo}</td>
                          <td style={{ padding: '6px 10px', color: 'var(--text-primary)' }}>{unmatchedName || <span style={{ color: 'var(--text-secondary)' }}>—</span>}</td>
                          <td style={{ padding: '6px 10px', color: 'var(--text-secondary)' }}>{u.DB}</td>
                          <td style={{ padding: '6px 10px' }}>
                            <span style={{ padding: '2px 7px', borderRadius: '4px', background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171', fontWeight: '600' }}>
                              {u.course.toUpperCase()}
                            </span>
                          </td>
                          <td style={{ padding: '6px 10px' }}>
                            <a
                              href={getSmsUrl(u.StudentNo, u.DB)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: 'var(--accent)', textDecoration: 'none', fontSize: '12px', fontWeight: '600' }}
                            >
                              View in SMS ↗
                            </a>
                          </td>
                          <td style={{ padding: '6px 10px' }}>
                            <button
                              title="Manually assign this student to a section"
                              onClick={() => {
                                setAssigningStudent({ StudentNo: u.StudentNo, DB: u.DB, course: u.course, first_name: u.first_name, last_name: u.last_name });
                                setStudentAssignTarget('');
                                setAssignError(null);
                              }}
                              style={{ padding: '3px 10px', borderRadius: '5px', border: '1px solid rgba(248,113,113,0.4)', background: 'rgba(248,113,113,0.08)', color: '#f87171', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}
                            >
                              + Assign
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Manual Assignment Modal (unmatched students) */}
            {assigningStudent && selectedRun && (
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.6)', zIndex: 9999,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
                onClick={e => { if (e.target === e.currentTarget) { setAssigningStudent(null); setAssignError(null); } }}
              >
                <div style={{
                  background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                  borderRadius: '12px', padding: '1.5rem', width: '500px', maxWidth: '95vw',
                }}>
                  <h3 style={{ margin: '0 0 0.5rem', fontSize: '16px', color: 'var(--text-primary)' }}>
                    Manually Assign Student to a Section
                  </h3>
                  <p style={{ margin: '0 0 1rem', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Student: <strong style={{ color: 'var(--text-primary)' }}>{assigningStudent.last_name && assigningStudent.first_name ? `${assigningStudent.last_name}, ${assigningStudent.first_name}` : assigningStudent.StudentNo}</strong>
                    {' '}({assigningStudent.DB}) &nbsp;·&nbsp; Course: <strong style={{ color: '#f87171' }}>{assigningStudent.course.toUpperCase()}</strong>
                  </p>
                  <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Assign to section:
                  </label>
                  <select
                    value={studentAssignTarget}
                    onChange={e => setStudentAssignTarget(e.target.value)}
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: '6px',
                      border: '1px solid var(--card-border)', background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)', fontSize: '13px', marginBottom: '1rem',
                    }}
                  >
                    <option value="">— Select a section —</option>
                    {selectedRun.sections
                      .filter(s => s.course.toLowerCase() === assigningStudent.course.toLowerCase())
                      .map(s => {
                        const instrInfo = s.instructor ? instructorLookup[s.instructor.toLowerCase()] : null;
                        const instrName = instrInfo?.name || s.instructor || 'Unassigned';
                        const slotLabel = s.slot ? ({ '1':'8:00 AM','2':'11:00 AM','3':'2:00 PM','4':'5:00 PM','5':'7:35 PM' } as Record<string,string>)[s.slot] ?? `Slot ${s.slot}` : 'No slot';
                        const overCapacity = s.enrolled >= s.capacity;
                        return (
                          <option key={s.section_id} value={s.section_id}>
                            {instrName} — {slotLabel} ({s.enrolled}/{s.capacity}){overCapacity ? ' ⚠ Over capacity' : ''}
                          </option>
                        );
                      })
                    }
                  </select>
                  {assignError && <p style={{ color: '#f87171', fontSize: '13px', margin: '0 0 0.75rem' }}>{assignError}</p>}
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => { setAssigningStudent(null); setAssignError(null); }}
                      style={{ ...BTN_SECONDARY, padding: '7px 16px', fontSize: '13px' }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleStudentAssign}
                      disabled={!studentAssignTarget || studentAssignPending}
                      style={{ ...BTN_PRIMARY, padding: '7px 16px', fontSize: '13px', opacity: (!studentAssignTarget || studentAssignPending) ? 0.6 : 1 }}
                    >
                      {studentAssignPending ? 'Assigning…' : 'Assign Student'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Student Move Modal */}
            {movingStudent && selectedRun && (
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.6)', zIndex: 9999,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
                onClick={e => { if (e.target === e.currentTarget) { setMovingStudent(null); setMoveCrossCourse(false); setForceOverCapacity(false); } }}
              >
                <div style={{
                  background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                  borderRadius: '12px', padding: '1.5rem', width: '520px', maxWidth: '95vw',
                }}>
                  <h3 style={{ margin: '0 0 0.75rem', fontSize: '16px', color: 'var(--text-primary)' }}>
                    Move Student to a Different Section
                  </h3>
                  <p style={{ margin: '0 0 0.75rem', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Student: <strong>{movingStudent.last_name && movingStudent.first_name ? `${movingStudent.last_name}, ${movingStudent.first_name}` : movingStudent.StudentNo}</strong>
                    {' '}({movingStudent.DB}) &nbsp;·&nbsp; Current course: <strong>{movingStudent.course.toUpperCase()}</strong>
                  </p>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '0.75rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={moveCrossCourse}
                      onChange={e => { setMoveCrossCourse(e.target.checked); setStudentMoveTarget(''); setForceOverCapacity(false); }}
                    />
                    Move to a <em>different course</em> (cross-course transfer)
                  </label>
                  <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    {moveCrossCourse ? 'Select target section (any course):' : 'Move to section (same course):'}
                  </label>
                  <select
                    value={studentMoveTarget}
                    onChange={e => { setStudentMoveTarget(e.target.value); setForceOverCapacity(false); }}
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: '6px',
                      border: '1px solid var(--card-border)', background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)', fontSize: '13px', marginBottom: '0.75rem',
                    }}
                  >
                    <option value="">— Select a section —</option>
                    {selectedRun.sections
                      .filter(s => moveCrossCourse
                        ? s.section_id !== movingStudent.from_section_id
                        : s.course.toLowerCase() === movingStudent.course.toLowerCase() && s.section_id !== movingStudent.from_section_id
                      )
                      .map(s => {
                        const instrInfo = s.instructor ? instructorLookup[s.instructor.toLowerCase()] : null;
                        const instrName = instrInfo?.name || s.instructor || 'Unassigned';
                        const slotLabel = s.slot ? ({ '1':'8:00 AM','2':'11:00 AM','3':'2:00 PM','4':'5:00 PM','5':'7:35 PM' } as Record<string,string>)[s.slot] ?? `Slot ${s.slot}` : 'No slot';
                        const atCap = s.enrolled >= s.capacity;
                        return (
                          <option key={s.section_id} value={s.section_id}>
                            {moveCrossCourse ? `[${s.course.toUpperCase()}] ` : ''}{instrName} — {slotLabel} ({s.enrolled}/{s.capacity}){atCap ? ' ⚠ At capacity' : ''}
                          </option>
                        );
                      })
                    }
                  </select>
                  {/* Capacity warning */}
                  {(() => {
                    const targetSec = studentMoveTarget ? selectedRun.sections.find(s => s.section_id === studentMoveTarget) : null;
                    const atCap = targetSec && targetSec.enrolled >= targetSec.capacity;
                    if (!atCap) return null;
                    return (
                      <div style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: '6px', padding: '8px 12px', marginBottom: '0.75rem', fontSize: '13px', color: '#f87171' }}>
                        ⚠ This section is at capacity ({targetSec!.enrolled}/{targetSec!.capacity}).
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', cursor: 'pointer', color: 'var(--text-primary)' }}>
                          <input
                            type="checkbox"
                            checked={forceOverCapacity}
                            onChange={e => setForceOverCapacity(e.target.checked)}
                          />
                          Override capacity limit and move anyway
                        </label>
                      </div>
                    );
                  })()}
                  {matrixEditError && <p style={{ color: '#f87171', fontSize: '13px', margin: '0 0 0.75rem' }}>{matrixEditError}</p>}
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => { setMovingStudent(null); setMoveCrossCourse(false); setForceOverCapacity(false); }}
                      style={{ ...BTN_SECONDARY, padding: '7px 16px', fontSize: '13px' }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleStudentMove}
                      disabled={(() => {
                        if (!studentMoveTarget || studentMovePending) return true;
                        const targetSec = selectedRun.sections.find(s => s.section_id === studentMoveTarget);
                        const atCap = targetSec && targetSec.enrolled >= targetSec.capacity;
                        return atCap && !forceOverCapacity;
                      })()}
                      style={{ ...BTN_PRIMARY, padding: '7px 16px', fontSize: '13px', opacity: (() => {
                        if (!studentMoveTarget || studentMovePending) return 0.6;
                        const targetSec = selectedRun.sections.find(s => s.section_id === studentMoveTarget);
                        const atCap = targetSec && targetSec.enrolled >= targetSec.capacity;
                        return atCap && !forceOverCapacity ? 0.6 : 1;
                      })() }}
                    >
                      {studentMovePending ? 'Moving…' : 'Move Student'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {!loadingRuns && runs.length === 0 && (
        <div style={{ ...CARD, textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: '48px', marginBottom: '1rem' }}>🗓️</div>
          <p>No scheduler runs yet. Set a term and click <strong>Run Scheduler</strong> to get started.</p>
        </div>
      )}

      {/* Delete Section Modal */}
      {deletingSectionTarget && selectedRun && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
          onClick={e => { if (e.target === e.currentTarget) { setDeletingSectionTarget(null); setDeleteSectionError(null); setDeleteSectionForce(false); } }}
        >
          <div style={{
            background: 'var(--card-bg)', border: '1px solid var(--card-border)',
            borderRadius: '12px', padding: '1.5rem', width: '480px', maxWidth: '95vw',
          }}>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '16px', color: '#f87171' }}>
              🗑 Delete Section
            </h3>
            <p style={{ margin: '0 0 0.75rem', fontSize: '13px', color: 'var(--text-secondary)' }}>
              Course: <strong style={{ color: 'var(--text-primary)' }}>{deletingSectionTarget.course.toUpperCase()}</strong>
              {' '}·{' '}
              Instructor: <strong style={{ color: 'var(--text-primary)' }}>{deletingSectionTarget.instructor ? (instructorLookup[deletingSectionTarget.instructor.toLowerCase()]?.name ?? deletingSectionTarget.instructor) : 'Unassigned'}</strong>
              {' '}·{' '}
              Slot: <strong style={{ color: 'var(--text-primary)' }}>{getSlotLabel(deletingSectionTarget.slot)}</strong>
            </p>
            {deletingSectionTarget.enrolled > 0 && (
              <div style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: '6px', padding: '10px 14px', marginBottom: '0.75rem', fontSize: '13px' }}>
                <div style={{ color: '#f87171', fontWeight: '600', marginBottom: '4px' }}>
                  ⚠ This section has {deletingSectionTarget.enrolled} enrolled student(s).
                </div>
                <div style={{ color: 'var(--text-secondary)' }}>
                  Deleting it will move them to the unmatched list. You can reassign them individually.
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', cursor: 'pointer', color: 'var(--text-primary)' }}>
                  <input
                    type="checkbox"
                    checked={deleteSectionForce}
                    onChange={e => setDeleteSectionForce(e.target.checked)}
                  />
                  I understand — proceed and move students to unmatched
                </label>
              </div>
            )}
            {deleteSectionError && <p style={{ color: '#f87171', fontSize: '13px', margin: '0 0 0.75rem' }}>{deleteSectionError}</p>}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setDeletingSectionTarget(null); setDeleteSectionError(null); setDeleteSectionForce(false); }}
                style={{ ...BTN_SECONDARY, padding: '7px 16px', fontSize: '13px' }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteSection(deleteSectionForce || deletingSectionTarget.enrolled === 0)}
                disabled={deleteSectionPending || (deletingSectionTarget.enrolled > 0 && !deleteSectionForce)}
                style={{ ...BTN_PRIMARY, padding: '7px 16px', fontSize: '13px', background: '#ef4444', borderColor: '#ef4444', opacity: (deleteSectionPending || (deletingSectionTarget.enrolled > 0 && !deleteSectionForce)) ? 0.6 : 1 }}
              >
                {deleteSectionPending ? 'Deleting…' : 'Delete Section'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Section Modal */}
      {showCreateSection && selectedRun && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
          onClick={e => { if (e.target === e.currentTarget) { setShowCreateSection(false); setCreateSectionError(null); } }}
        >
          <div style={{
            background: 'var(--card-bg)', border: '1px solid var(--card-border)',
            borderRadius: '12px', padding: '1.5rem', width: '480px', maxWidth: '95vw',
          }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '16px', color: 'var(--text-primary)' }}>
              ＋ Create New Section
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '3px' }}>Course code *</label>
                <input
                  value={createSectionCourse}
                  onChange={e => setCreateSectionCourse(e.target.value.toUpperCase())}
                  placeholder="e.g. BUS101"
                  style={{ width: '100%', padding: '7px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '13px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '3px' }}>Instructor (optional)</label>
                <select
                  value={createSectionInstructor}
                  onChange={e => setCreateSectionInstructor(e.target.value)}
                  style={{ width: '100%', padding: '7px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '13px' }}
                >
                  <option value="">— None (unstaffed) —</option>
                  {[...instructorList].sort((a, b) => a.name.localeCompare(b.name)).map(i => (
                    <option key={i.row_key} value={i.email}>{i.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '3px' }}>Time slot (optional)</label>
                <select
                  value={createSectionSlot}
                  onChange={e => setCreateSectionSlot(e.target.value)}
                  style={{ width: '100%', padding: '7px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '13px' }}
                >
                  <option value="">— No slot —</option>
                  <option value="1">Slot 1 — 8:00 AM</option>
                  <option value="2">Slot 2 — 11:00 AM</option>
                  <option value="3">Slot 3 — 2:00 PM</option>
                  <option value="4">Slot 4 — 5:00 PM</option>
                  <option value="5">Slot 5 — 7:35 PM</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '3px' }}>Capacity</label>
                <input
                  type="number"
                  value={createSectionCapacity}
                  onChange={e => setCreateSectionCapacity(e.target.value)}
                  min={1}
                  max={200}
                  style={{ width: '100%', padding: '7px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '13px', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            {createSectionError && <p style={{ color: '#f87171', fontSize: '13px', margin: '0 0 0.75rem' }}>{createSectionError}</p>}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowCreateSection(false); setCreateSectionError(null); }}
                style={{ ...BTN_SECONDARY, padding: '7px 16px', fontSize: '13px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSection}
                disabled={!createSectionCourse.trim() || createSectionPending}
                style={{ ...BTN_PRIMARY, padding: '7px 16px', fontSize: '13px', opacity: (!createSectionCourse.trim() || createSectionPending) ? 0.6 : 1 }}
              >
                {createSectionPending ? 'Creating…' : 'Create Section'}
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}

        </>
      )}

      {/* Jump-to-top button — visible when any expandable panel is open */}
      {(showSections || showInstructorMatrix || showStudentMatrix) && (
        <button
          onClick={() => {
            if (topRef.current) {
              topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              document.documentElement.scrollTop = 0;
              document.body.scrollTop = 0;
            }
          }}
          title="Back to top"
          style={{
            position: 'fixed',
            bottom: '28px',
            right: '28px',
            zIndex: 1000,
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            border: '1px solid var(--card-border)',
            background: 'var(--card-bg)',
            color: 'var(--accent)',
            fontSize: '20px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 12px rgba(0,0,0,0.35)',
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
          onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
        >
          ↑
        </button>
      )}
    </div>
  );
}
